
import type { RiderSkillClipName, SkillClipName } from "./skillclip";
import { hiddenAt } from "./motionhide";
import { oneShotLengthS, oneShotLocalS } from "./motionloop";
import { FULL_SHADE } from "./shade";
import { HEADING_STEPS } from "../sim/fixed";
import { Gait } from "../sim/movement";
import type { CreaturePalette } from "./creature";
import { GlError, checkGlError } from "./gl/context";
import { createBuffer, createVertexArray, describeAttributes, type AttributeSpec } from "./gl/buffer";
import { createProgram, uniformLocations } from "./gl/shader";
import { modulateColour } from "./mobcolor";
import { CHARACTER_SHADOW_TEXTURE_FACTOR, type CharacterShadowMap } from "./charactershadow";
import { weaponTraceLengthMm } from "./weapontrail";
import { SUN_DIR_GLSL } from "./gl/sun";
import { loadImage } from "./gl/texture";
import type { MapEnvironment } from "./mapassets";
import { canUseEntityGlLayer } from "./entitylayer";
import { identity, multiply, type Mat4 } from "./view";

const BASE_URL = "creaturemesh/";
const INDEX_URL = `${BASE_URL}index.json`;
const BIN_URL = `${BASE_URL}creaturemesh.bin`;
/** A backgrounded Safari tab can interrupt one fetch while preserving the
 *  document and its shared WebGL context. One delayed retry keeps that
 *  transient from permanently turning every skinned actor into a silhouette. */
export const ANIMATION_FETCH_RETRIES = 1;
export const ANIMATION_RETRY_DELAY_MS = 250;

export async function fetchAnimationWithRetry(
  request: () => Promise<Response>,
  wait: (ms: number) => Promise<void> = (ms) => new Promise((resolve) => setTimeout(resolve, ms)),
  url = "creature animation",
): Promise<Int16Array> {
  let failure: unknown;
  for (let attempt = 0; attempt <= ANIMATION_FETCH_RETRIES; attempt += 1) {
    try {
      const response = await request();
      if (!response.ok) throw new Error(`${url}: HTTP ${response.status}`);
      return new Int16Array(await response.arrayBuffer());
    } catch (error) {
      failure = error;
      if (attempt === ANIMATION_FETCH_RETRIES) break;
      await wait(ANIMATION_RETRY_DELAY_MS);
    }
  }
  throw failure;
}

/** A backgrounded Safari tab can interrupt the creature index or geometry blob
 *  fetch while preserving the shared terrain context. Retry the required pair
 *  once so that interruption does not leave every actor on the silhouette path;
 *  a persistent missing pack still reports unavailable through `status()`. */
export const CREATURE_PACK_FETCH_RETRIES = 1;
export const CREATURE_PACK_RETRY_DELAY_MS = 250;

export async function fetchCreaturePackWithRetry<T>(
  loadPack: () => Promise<T>,
  wait: (milliseconds: number) => Promise<void> = (milliseconds) =>
    new Promise((resolve) => setTimeout(resolve, milliseconds)),
): Promise<T> {
  let failure: unknown;
  for (let attempt = 0; attempt <= CREATURE_PACK_FETCH_RETRIES; attempt += 1) {
    try {
      return await loadPack();
    } catch (error) {
      failure = error;
      if (attempt === CREATURE_PACK_FETCH_RETRIES) break;
      await wait(CREATURE_PACK_RETRY_DELAY_MS);
    }
  }
  throw failure;
}

/** A backgrounded Safari tab can interrupt one skin decode while preserving
 * the shared terrain context. Retry once so a real mesh does not stay forever
 * on the flat-tint path; a persistent missing skin still uses that fallback. */
export const CREATURE_TEXTURE_FETCH_RETRIES = 1;
export const CREATURE_TEXTURE_RETRY_DELAY_MS = 250;

export async function loadCreatureTextureWithRetry<T>(
  loadTexture: () => Promise<T>,
  wait: (ms: number) => Promise<void> = (ms) => new Promise((resolve) => setTimeout(resolve, ms)),
): Promise<T> {
  let failure: unknown;
  for (let attempt = 0; attempt <= CREATURE_TEXTURE_FETCH_RETRIES; attempt += 1) {
    try {
      return await loadTexture();
    } catch (error) {
      failure = error;
      if (attempt === CREATURE_TEXTURE_FETCH_RETRIES) break;
      await wait(CREATURE_TEXTURE_RETRY_DELAY_MS);
    }
  }
  throw failure;
}

interface MeshEntry {
  readonly texture: string | null;
  readonly indexCount: number;
  readonly indexType: "uint16" | "uint32";
  readonly vertexOffset: number;
  readonly indexOffset: number;
  readonly vertexByteLength?: number;
  readonly indexByteLength?: number;
  readonly vertexCount?: number;
  /** Mesh-local bounds in millimetres, as `mesh_pack.py` writes them. Only the
   *  z extent is read here, by `heightMm`, and only so a nameplate can clear a
   *  character's head. */
  readonly bboxMin: readonly [number, number, number];
  readonly bboxMax: readonly [number, number, number];
}

/** One sampled animation clip, as `tools/build_creature_meshes.py` writes it.
 *  `offset` is a byte offset into its skin's `anim` shard; `strideMm` is 0 for a
 *  clip whose own files never said how far it travels (see `strideOf`). */
export interface ClipEntry {
  readonly offset: number;
  readonly frames: number;
  readonly durationS: number;
  readonly strideMm: number;
  readonly positionScale: number;
  readonly loop?: boolean;
  /** Present only on a clip whose model stores thirteen numbers a bone rather
   *  than seven -- what one int16 of the symmetric 3x3 stands for. See
   *  `SkinEntry.encoding`. */
  readonly shearScale?: number;
  readonly hitStartS?: number;
  readonly hitEndS?: number;
  /** `Group ComboInputData` from the motion's own `.msa`
   *  (`RaceMotionData.cpp:354-363`). Seconds from the clip's start. Absent
   *  on every clip that is not a combo step. The live hit is still
   *  server-owned -- these only time the client's combo-animation window. */
  readonly preInputS?: number;
  readonly directInputS?: number;
  readonly inputLimitS?: number;
  /** Authored `LinkTime`; the original client loader does not read it. */
  readonly linkS?: number;
  /** `AttackingBone` / `WeaponLength` / `List HitPosition` from the same
   *  AttackingData block the hit window comes from (`GameType.cpp:42-74`).
   *  Length is art units (×10 = mm). Tip samples are presentation only --
   *  this client never asserts a hit from them. */
  readonly attackingBone?: string;
  readonly weaponLength?: number;
  readonly hitPositions?: readonly HitPositionSample[];
  /** `HittingType` of the blow this motion lands (`GameType.h:44-49`), from
   *  whichever block lands it -- see `read_msa_hitting_type`. */
  readonly hittingType?: number;
  /** Every `MotionEventType 2` block (`RaceMotionDataEvent.h:28-49`): seconds
   *  from the clip's start, seconds of waving, power and range in legacy units. */
  readonly screenWaving?: readonly ScreenWavingEvent[];
  /** Present, true, on a motion carrying a fly, warp or effect-to-target event
   *  (`RaceMotionData.h:168-172`): its caster's target is held while it plays
   *  (`ActorInstanceFly.cpp:111-116`). */
  readonly holdsTarget?: boolean;
  /** `Group LoopData` (`RaceMotionData.cpp:372-386`): how many passes the
   *  motion makes through `loopStartS`-`loopEndS` (-1 without end), and whether
   *  a skill inside it may be cancelled. See `oneShotLocalS`. */
  readonly loopCount?: number;
  readonly loopCancel?: boolean;
  readonly loopStartS?: number;
  readonly loopEndS?: number;
  /** Starting times of the motion's character hide, show and warp events
   *  (`RaceMotionData.h:169-171`). See `hiddenAt`. */
  readonly hideS?: readonly number[];
  readonly showS?: readonly number[];
  readonly warpS?: readonly number[];
  /** This file's weight in its motion's pick, on a clip with variants
   *  `name~1`, `name~2`, ... (`ActorInstanceMotion.cpp:598-621`). */
  readonly weight?: number;
  /** Present only where this clip reflects other bones than
   *  `SkinEntry.mirroredBones` does -- a bone only an attachment hangs off,
   *  whose whole world matrix the weapon takes (`ThingInstance.cpp:621-630`). */
  readonly mirroredBones?: readonly number[];
}

/** The palette slots `poseToBoneTexels` reflects while `clip` plays. */
export function clipMirroredBones(skin: SkinEntry, clip: ClipEntry): readonly number[] {
  return clip.mirroredBones ?? skin.mirroredBones ?? [];
}

/** One authored screen wave; see `ClipEntry.screenWaving`. */
export interface ScreenWavingEvent {
  readonly t: number;
  readonly durationS: number;
  readonly power: number;
  readonly range: number;
}

/** One `List HitPosition` row: motion time plus previous and current tip. */
export interface HitPositionSample {
  readonly t: number;
  readonly last: readonly [number, number, number];
  readonly pos: readonly [number, number, number];
}

export interface AttachEntry {
  /** The skeleton bone's own name, for the log line and for nothing else. */
  readonly bone: string;
  /** Which palette slot of this model's bone frames carries it. */
  readonly slot: number;
  readonly bindWorldMm: readonly number[];
}

export interface SkinEntry {
  /** The animation shard beside `index.json` that this skin's clip offsets
   *  index (`build_animation_shards` in `tools/build_creature_meshes.py`). */
  readonly anim: string;
  readonly boneCount: number;
  readonly mirroredBones?: readonly number[];
  readonly encoding?: "rigid" | "affine";
  readonly clips: Readonly<Partial<Record<ClipName, ClipEntry>>>;
  readonly attach?: Readonly<Record<string, AttachEntry>>;
  /** Parts the pack could not place on this body, each with the reason. Never
   *  somewhere to hang anything: it is here so that a body with no hand says
   *  why rather than merely having no `attach`. */
  readonly attachUnresolved?: readonly string[];
}

export interface RaceEntry {
  readonly job: string;
  /** `"m"` or `"w"`, the original's own two. */
  readonly sex: string;
  /** The `models`-key prefix this race's bodies are named under -- `pc/warrior`
   *  or `pc2/shaman`, the pack folder its art comes out of. Informational:
   *  `raceBodies` already carries the keys themselves. */
  readonly prefix: string;
  /** The shape a character with no armour on is in. */
  readonly baseShape: number;
  readonly weaponBone: string;
  /** Absent for the warrior and the sura, which register no `PART_WEAPON_LEFT`
   *  bone at all (`playersettingmodule.py:678,1016` against `:891,1208`). */
  readonly weaponBoneLeft?: string;
}

export interface CreatureIndex {
  readonly vertexStride: number;
  readonly models: Readonly<Record<string, { readonly meshes: readonly MeshEntry[] }>>;
  readonly textures: Readonly<Record<string, string>>;
  readonly playerKey: string;
  readonly skins?: Readonly<Record<string, SkinEntry>>;
  readonly maxBoneCount?: number;
  readonly raceHair?: Readonly<Record<string, string>>;
  /** `COSTUME_HAIR` vnum -> the `PART_HAIR` shape it publishes -- its proto
   *  VALUE3, not its vnum (`item.cpp:754-762`). `armourShapes` for hair, and
   *  needed for the same reason: the local player is dressed from his own
   *  `Inventory` rather than from the snapshot that would carry the part. */
  readonly hairShapes?: Readonly<Record<string, number>>;
  /** Item vnum (as a string, because JSON objects have string keys) -> the
   *  key in `models` its geometry sits under. Several vnums share one entry,
   *  which is the original's own sharing: `item_list.txt` gives every
   *  `+0..+4` of a sword the same `.gr2`. */
  readonly itemModels?: Readonly<Record<string, string>>;
  /** Guild building race (as a string) -> the key in `models` it draws as:
   *  the race named after `guildbuildinglist.txt`'s MODEL column
   *  (`root/playersettingmodule.py:1339-1340`). */
  readonly buildingModels?: Readonly<Record<string, string>>;
  /** Pet mob vnum -> the npclist race name its model is keyed by. */
  readonly petModels?: Readonly<Record<string, string>>;
  /** Weapon vnum -> the `attach` parts the original hangs it on. A dagger is
   *  in both hands, a bow is in the left one only, everything else is in the
   *  right -- `__IsRightHandWeapon`/`__IsLeftHandWeapon` are not each other's
   *  negation and this table is their answer. */
  readonly itemHands?: Readonly<Record<string, readonly string[]>>;
  /** Weapon vnum -> conditional `attach` parts while mounted. Rows are only
   *  present where the mounted answer differs from `itemHands` (fans). */
  readonly itemMountedHands?: Readonly<Record<string, readonly string[]>>;
  /** Weapon vnum -> the classes whose characters may hold it, out of the
   *  proto's anti-flags. Nothing on the wire says what a vnum is legal for. */
  readonly itemJobs?: Readonly<Record<string, readonly string[]>>;
  /** Weapon vnum -> the authored one-shot clip selected by its subtype. The
   *  server-owned worn vnum is the input; this local table is only art. */
  readonly itemAttackClips?: Readonly<Record<string, ClipName>>;
  readonly bodyModels?: Readonly<Record<string, string>>;
  /** Race number -> shape number -> the `models` key of that body. The whole
   *  wardrobe: eight bare bodies and one per (race, shape) an armour this
   *  build defines can put a character in. */
  readonly raceBodies?: Readonly<Record<string, Readonly<Record<string, string>>>>;
  /** Body-armour vnum -> the shape number `__ArmorVnumToShape` reads out of
   *  it (`UserInterface/InstanceBase.cpp:280-304`, `VALUE3`). Separate from
   *  `raceBodies` because the original separates them: the vnum knows nothing
   *  about the wearer and the race data knows nothing about items. */
  readonly armourShapes?: Readonly<Record<string, number>>;
  /** Body-armour vnum -> the classes that may wear it. */
  readonly armourJobs?: Readonly<Record<string, readonly string[]>>;
  /** Race number -> what that race is. */
  readonly races?: Readonly<Record<string, RaceEntry>>;
  /** Where the costume half of the wardrobe went, and what it costs. Absent on
   *  a pack built without costume bodies, which is why the client asks the
   *  index rather than hard-coding the two filenames. See `loadCostumePack`. */
  readonly costumePack?: CostumePackRef;
}

/** The `costumePack` row `tools/build_creature_meshes.py` writes into the main
 *  index (`COSTUME_BIN_NAME`/`COSTUME_INDEX_NAME`). `models` and `byteLength`
 *  are what the second pack costs, published so a build can be measured
 *  without fetching it. */
export interface CostumePackRef {
  readonly index: string;
  readonly bin: string;
  readonly models: number;
  readonly byteLength: number;
}

export const PLAYER_MODEL_KEY = "player";

export function hairEntityId(entityId: number): number {
  return entityId | 0x40000000;
}

/** As much of `index.json` as the wardrobe lookups below read. Structural, so
 *  a test can hand them the shipped file without building a renderer -- the
 *  renderer needs a WebGL2 context and the question "which body is this
 *  armour on this race" needs none. */
export interface WardrobeIndex {
  readonly models: Readonly<Record<string, unknown>>;
  readonly bodyModels?: Readonly<Record<string, string>>;
  readonly raceBodies?: Readonly<Record<string, Readonly<Record<string, string>>>>;
  readonly armourShapes?: Readonly<Record<string, number>>;
  readonly armourJobs?: Readonly<Record<string, readonly string[]>>;
  readonly races?: Readonly<Record<string, RaceEntry>>;
  readonly raceHair?: Readonly<Record<string, string>>;
  readonly hairShapes?: Readonly<Record<string, number>>;
}

/** The source-authored default hair part for a race, or null when a pack made
 * before HairData was imported is being served. `CActorInstance::SetHair`
 * registers this as `PART_HAIR` beside the body (`ActorInstanceData.cpp:
 * 124-153`), so silently substituting the face material is not equivalent. */
export function defaultHairKeyIn(index: WardrobeIndex, race: number): string | null {
  const key = index.raceHair?.[String(race)];
  return key !== undefined && index.models[key] !== undefined ? key : null;
}

interface CostumeHairIndex {
  readonly raceHair?: Readonly<Record<string, Readonly<Record<string, string>>>>;
}

export function costumeHairKeyIn(
  index: CreatureIndex,
  race: number,
  hairPart: number,
): string | null {
  if (!Number.isFinite(hairPart) || hairPart < COSTUME_HAIR_SHAPE_MIN) return null;
  const pending = loadCostumePack(index);
  if (pending === null) return null;
  const costume = resolvedCostumePack;
  if (costume === null) return null;
  const rows = (costume.index as unknown as CostumeHairIndex).raceHair;
  const key = rows?.[String(race)]?.[String(hairPart)];
  return key !== undefined && costume.index.models[key] !== undefined ? key : null;
}

/** The `PART_HAIR` shape a `COSTUME_HAIR` vnum publishes, or 0 for anything
 *  else. `metin3/server/equipment.py::costume_hair_part` is the same read on
 *  the server; the client needs its own because the local player is dressed
 *  from his `Inventory`, which says a vnum and never a part. */
export function hairShapeForVnumIn(index: WardrobeIndex, vnum: number): number {
  return index.hairShapes?.[String(vnum)] ?? 0;
}

/** The band a costume hair shape lives in. `PART_HAIR_COSTUME_MIN` in
 *  `metin3/server/specialuse.py` and `COSTUME_HAIR_SHAPE_MIN` in
 *  `tools/build_creature_meshes.py` are the same number: below it are the six
 *  character-creation hairs, which are in the main pack. */
export const COSTUME_HAIR_SHAPE_MIN = 1001;

/** The body a race is in with no armour on. See `baseBodyKey`. */
export function baseBodyKeyIn(index: WardrobeIndex, race: number): string | null {
  const entry = index.races?.[String(race)];
  if (entry === undefined) return null;
  return shapeKeyIn(index, race, entry.baseShape);
}

export function bodyModelKeyIn(index: WardrobeIndex, vnum: number, race: number = 0): string | null {
  const shape = index.armourShapes?.[String(vnum)];
  const entry = index.races?.[String(race)];
  if (shape !== undefined && entry !== undefined) {
    // The same shape number can be reachable through different class armour.
    // The art table answers whether the race owns that shape; the item proto's
    // anti-flags answer whether this particular item may put the race in it.
    // Keep the two source-owned facts composed at lookup time.
    const jobs = index.armourJobs?.[String(vnum)];
    if (jobs !== undefined && !jobs.includes(entry.job)) return null;
    return shape === entry.baseShape ? null : shapeKeyIn(index, race, shape);
  }
  const key = index.bodyModels?.[String(vnum)];
  return key !== undefined && index.models[key] !== undefined ? key : null;
}

function shapeKeyIn(index: WardrobeIndex, race: number, shape: number): string | null {
  const key = index.raceBodies?.[String(race)]?.[String(shape)];
  return key !== undefined && index.models[key] !== undefined ? key : null;
}

export type ClipName =
  | SkillClipName
  | RiderSkillClipName
  | "idle"
  | "walk"
  | "run"
  | "attack"
  | ComboClipName
  | "dead"
  | "damage"
  | "knockdown"
  | "standUp"
  | "knockdownBack"
  | "standUpBack"
  | "damageBack"
  | "spawn"
  | "special1"
  | "special2"
  | "special3"
  | "special4"
  | "special5"
  | "dig"
  | GestureClipName
  | FishingClipName
  | "weddingIdle"
  | "weddingWalk"
  | "horseIdle"
  | "horseWalk"
  | "horseRun";

/** The gesture clips sampled from each race's `action/` folder
 *  (`emotion.py:84-120`), named by file: `french_kiss_with_sura.msa` is
 *  `frenchKissWithSura`. */
export type GestureClipName =
  | "clap" | "cheers1" | "cheers2"
  | "dance1" | "dance2" | "dance3" | "dance4" | "dance5" | "dance6"
  | "congratulation" | "forgive" | "angry" | "attractive" | "sad" | "shy"
  | "cheerup" | "banter" | "joy"
  | "kissWithWarrior" | "kissWithAssassin" | "kissWithSura" | "kissWithShaman"
  | "frenchKissWithWarrior" | "frenchKissWithAssassin" | "frenchKissWithSura" | "frenchKissWithShaman"
  | "slapHit" | "slapHurt";

/** Each weapon motion mode's combo clips, one per `combo_0<n>.msa`: `attack<stem>`
 *  for the first file and `attack<stem><n>` after it (`WEAPON_COMBO_MODES` in
 *  `tools/build_creature_meshes.py`). */
export type ComboStem =
  | "OneHand" | "TwoHand" | "DualHand" | "Fan" | "Bell"
  | "HorseOneHand" | "HorseTwoHand" | "HorseDualHand" | "HorseFan" | "HorseBell";
export type ComboClipName = `attack${ComboStem}` | `attack${ComboStem}${2 | 3 | 4 | 5 | 6 | 7 | 8}`;

/** `MOTION_MODE_FISHING`'s nine clips (`playersettingmodule.py:625-636`). */
export type FishingClipName =
  | "fishingIdle" | "fishingWalk" | "fishingRun" | "fishingThrow" | "fishingWait"
  | "fishingStop" | "fishingReact" | "fishingCatch" | "fishingFail";

/** Which loops a body is drawn in: `RefreshState`'s general, fishing-rod,
 *  wedding-dress and horse modes (`InstanceBase.cpp:2866-2896`), the fishing
 *  wait a cast line holds (`InstanceBaseMotion.cpp:66-67,76-77`), and a miner,
 *  who keeps the general loops under his repeated dig
 *  (`PythonNetworkStreamPhaseGame.cpp:4358-4359`). */
export type MotionMode = "general" | "fishing" | "fishingLine" | "wedding" | "digging" | "horse";

type LoopName = "idle" | "walk" | "run";

const MODE_LOOPS: Readonly<Record<MotionMode, Readonly<Record<LoopName, ClipName>>>> = {
  general: { idle: "idle", walk: "walk", run: "run" },
  fishing: { idle: "fishingIdle", walk: "fishingWalk", run: "fishingRun" }, // playersettingmodule.py:628-630
  fishingLine: { idle: "fishingWait", walk: "fishingWalk", run: "fishingRun" }, // playersettingmodule.py:632
  wedding: { idle: "weddingIdle", walk: "weddingWalk", run: "weddingWalk" }, // emotion.py:244-246
  digging: { idle: "idle", walk: "walk", run: "run" },
  horse: { idle: "horseIdle", walk: "horseWalk", run: "horseRun" }, // playersettingmodule.py:641-645
};

/** The clip `mode` draws for one of the three general loops. */
export function modeLoopClip(mode: MotionMode, loop: LoopName): ClipName {
  return MODE_LOOPS[mode][loop];
}

/** Loops a body plays standing still, phased by the wall clock as `idle` is. */
const STANDING_LOOPS: ReadonlySet<ClipName> = new Set<ClipName>(["idle", "fishingIdle", "fishingWait", "weddingIdle", "horseIdle"]);

const COMBO_STEMS: ReadonlySet<string> = new Set<ComboStem>([
  "OneHand", "TwoHand", "DualHand", "Fan", "Bell",
  "HorseOneHand", "HorseTwoHand", "HorseDualHand", "HorseFan", "HorseBell",
]);

/** Whether a one-shot is an attack, in any weapon or riding mode:
 *  `NAME_NORMAL_ATTACK` or one of `NAME_COMBO_ATTACK_1..8` (`ActorInstanceMotion.cpp:479-487`). */
export function isAttackClip(clip: ClipName): boolean {
  if (clip === "attack") return true;
  const step = /^attack([A-Za-z]+?)[2-8]?$/.exec(clip);
  return step !== null && COMBO_STEMS.has(step[1]!);
}

/** `__IsKnockDownMotion`: the motion types `TYPE_KNOCKDOWN` names
 *  (`ActorInstanceMotion.cpp:895-898`, `RaceMotionData.cpp:52-54`). */
export function isKnockDownClip(clip: ClipName): boolean {
  return clip === "knockdown" || clip === "knockdownBack";
}

/** `__IsStandUpMotion` (`ActorInstanceMotion.cpp:908-911`, `RaceMotionData.cpp:56-58`). */
export function isStandUpClip(clip: ClipName): boolean {
  return clip === "standUp" || clip === "standUpBack";
}

/** `__IsDamageMotion`: `NAME_DAMAGE` and `NAME_DAMAGE_BACK` are both
 *  `TYPE_DAMAGE` (`ActorInstanceMotion.cpp:890-893`, `RaceMotionData.cpp:48-50`). */
export function isDamageClip(clip: ClipName): boolean {
  return clip === "damage" || clip === "damageBack";
}

/** `GetRandomMotionKey`: the file of a motion a roll in 0-99 plays, the first
 *  the roll less each weight in turn goes below zero at, and the first file
 *  where none does or the motion has one (`ActorInstanceMotion.cpp:598-621`). */
export function pickVariant(weights: readonly number[], roll: number): number {
  if (weights.length <= 1) return 0; // ActorInstanceMotion.cpp:598
  let left = roll;
  for (let i = 0; i < weights.length; i++) {
    left -= weights[i]!; // ActorInstanceMotion.cpp:604
    if (left < 0) return i;
  }
  return 0;
}

/** The weights of `clip`'s files on `skin`, first file first, or none where
 *  the clip has no variants `clip~1`, `clip~2`, .... */
export function variantWeights(skin: Pick<SkinEntry, "clips">, clip: ClipName): number[] {
  const clips = skin.clips as Readonly<Record<string, ClipEntry | undefined>>;
  const first = clips[clip];
  if (first?.weight === undefined) return [];
  const weights = [first.weight];
  for (let i = 1; clips[`${clip}~${i}`] !== undefined; i++) weights.push(clips[`${clip}~${i}`]!.weight ?? 0);
  return weights;
}

const COMBO_CLIP = /^attack((?:Horse)?(?:OneHand|TwoHand|DualHand|Fan|Bell))([2-8])?$/;

/** `COMBO_TYPE_1`'s length, the type `__GetCurrentComboType` plays mounted and
 *  without the combo skill (`ActorInstance.cpp:164-174`): four files on foot,
 *  three mounted, each step the file of its own number
 *  (`playersettingmodule.py:563-567,604-608,787-791,1053-1057,1093-1097`;
 *  `:657-660,670-673,867-870,1189-1192,1201-1204`). */
export const FOOT_COMBO_STEPS = 4;
const MOUNTED_COMBO_STEPS = 3;
/** `__OnEndCombo` sets a mounted combo's index back to one, so the step after
 *  its last is its second (`ActorInstanceBattle.cpp:257,299-305`). */
export const LOOPED_COMBO_STEP = 2;

/** The steps of a combo mode's first clip, in order, or null for a clip that
 *  starts no combo. */
export function comboChainOf(base: ClipName): readonly ClipName[] | null {
  const found = COMBO_CLIP.exec(base);
  if (found === null || found[2] !== undefined) return null;
  const stem = found[1]!;
  const steps = stem.startsWith("Horse") ? MOUNTED_COMBO_STEPS : FOOT_COMBO_STEPS;
  return Array.from({ length: steps }, (_, i) => (i === 0 ? base : `attack${stem}${i + 1}`) as ClipName);
}

/** Each weapon mode's combo types as clip names in step order, keyed by the
 *  mode's first clip: `attackdata.json`'s `combos` row for one race, as
 *  `RegisterComboAttackNew` strings them (`playersettingmodule.py:563-577`). */
export type ComboChains = Readonly<Record<string, readonly (readonly string[])[]>>;

/** The steps `base`'s mode plays as combo type `comboType`
 *  (`GetComboDataPointer`, `RaceData.cpp:457-466`), the first type's own
 *  numbered files where `chains` names none of that type
 *  (`playersettingmodule.py:563-567`), or null for a clip that starts no combo. */
export function comboChainFor(base: ClipName, comboType: number, chains?: ComboChains): readonly ClipName[] | null {
  const first = comboChainOf(base);
  if (first === null) return null;
  return (chains?.[base]?.[comboType] as readonly ClipName[] | undefined) ?? first;
}

/** A weapon's first combo clip as its rider plays it, `MODE_HORSE_<weapon>`
 *  (`InstanceBase.cpp:2896-2923`); any other clip unchanged. */
export function mountedAttackClip(base: ClipName): ClipName {
  const found = COMBO_CLIP.exec(base);
  if (found === null || found[2] !== undefined || found[1]!.startsWith("Horse")) return base;
  return `attackHorse${found[1]!}` as ClipName;
}

/** The clip `MOTION_COMBO_ATTACK_motion` is in `base`'s mode, a combo's
 *  first clip: `combo_01` the bare clip, `combo_0n` its n-numbered file
 *  (`playersettingmodule.py:563-577`); `base` itself for a clip that starts no
 *  combo or a motion below one. */
export function comboMotionClip(base: ClipName, motion: number): ClipName {
  if (comboChainOf(base) === null || motion <= 1) return base;
  return `${base}${motion}` as ClipName;
}

export function nextComboClip(base: ClipName, step: number, chain: readonly ClipName[] | null = comboChainOf(base)): ClipName {
  if (chain === null) return base;
  if (base.startsWith("attackHorse") && step > chain.length) {
    return chain[LOOPED_COMBO_STEP - 1 + ((step - LOOPED_COMBO_STEP) % (chain.length - LOOPED_COMBO_STEP + 1))]!;
  }
  const index = ((step - 1) % chain.length + chain.length) % chain.length;
  return chain[index]!;
}

export function comboInputLimitMs(entry: ClipEntry | null | undefined): number | null {
  if (entry === undefined || entry === null || entry.inputLimitS === undefined) return null;
  return entry.inputLimitS * 1_000;
}

export function comboChainGapMs(entry: ClipEntry | null | undefined): number | null {
  if (entry === undefined || entry === null || entry.inputLimitS === undefined) return null;
  const inputLimitMs = entry.inputLimitS * 1_000;
  const directMs = (entry.directInputS ?? entry.inputLimitS) * 1_000;
  return Math.max(inputLimitMs, directMs) * 2;
}

export function comboSwingIntervalMs(
  entry: ClipEntry | null | undefined, attackSpeed: number,
): number | null {
  if (entry === undefined || entry === null) return null;
  if (entry.inputLimitS === undefined || entry.directInputS === undefined) return null;
  const ratio = attackSpeed > 0 ? attackSpeed / 100 : 1;
  // A press inside the input window chains at once (`ActorInstanceBattle.cpp:177-191`);
  // one after PreInputTime is held until DirectInputTime (`:192-197`, `:227-239`).
  const chains = entry.directInputS <= entry.inputLimitS
    || (entry.preInputS !== undefined && entry.preInputS < entry.inputLimitS);
  const authoredS = chains ? Math.min(entry.directInputS, entry.durationS) : entry.durationS; // past its end the step returns to wait and __ClearCombo restarts the chain (ActorInstanceBattle.cpp:309-314)
  return Math.round((authoredS * 1_000) / ratio);
}

/** Art-unit → millimetre factor used for `.msa` Accumulation and WeaponLength
 *  (the same centimetres the APX census cites: WeaponLength 130 → 1_300 mm). */
export const MSA_ART_UNIT_TO_MM = 10;

/** `WeaponLength` from the clip's AttackingData, in millimetres, or null. */
export function weaponLengthMm(entry: ClipEntry | null | undefined): number | null {
  if (entry === undefined || entry === null || entry.weaponLength === undefined) return null;
  return entry.weaponLength * MSA_ART_UNIT_TO_MM;
}

export function hitTipHeightMm(entry: ClipEntry | null | undefined): number | null {
  const samples = entry?.hitPositions;
  if (samples === undefined || samples.length === 0) return null;
  let sum = 0;
  for (const sample of samples) sum += sample.pos[2] ?? 0;
  return (sum / samples.length) * MSA_ART_UNIT_TO_MM;
}

/** What a skin missing `clip` plays instead: a combo step its mode's first clip,
 *  a mounted mode's first clip its weapon's on foot, and that the plain attack. */
function clipFallback(clip: ClipName): ClipName | undefined {
  const found = COMBO_CLIP.exec(clip);
  if (found === null) return undefined;
  if (found[2] !== undefined) return `attack${found[1]!}` as ClipName;
  if (found[1]!.startsWith("Horse")) return `attack${found[1]!.slice("Horse".length)}` as ClipName;
  return "attack";
}

export function clipForSkin(skin: Pick<SkinEntry, "clips">, clip: ClipName): ClipName | null {
  let candidate: ClipName | undefined = clip;
  while (candidate !== undefined) {
    if (skin.clips[candidate] !== undefined) return candidate;
    candidate = clipFallback(candidate);
  }
  return null;
}

export function weaponAttackClipIn(
  index: Pick<CreatureIndex, "itemAttackClips">,
  vnum: number,
): ClipName {
  return index.itemAttackClips?.[String(vnum)] ?? "attack";
}

export function clipDurationMsIn(
  index: Pick<CreatureIndex, "skins">,
  key: string,
  clip: ClipName,
): number {
  const skin = index.skins?.[key];
  if (skin === undefined) return 0;
  const playing = clipForSkin(skin, clip);
  const entry = playing === null ? undefined : skin.clips[playing];
  return entry === undefined ? 0 : entry.durationS * 1000;
}

export function attachSlot(
  attachments: Readonly<Record<string, AttachEntry>>,
  part: string,
): AttachEntry | undefined {
  const named = attachments[part];
  if (named !== undefined) return named;
  if (part === "weapon") return attachments.weaponLeft;
  if (part === "weaponLeft") return attachments.weapon;
  return undefined;
}


export type MeshStatus =
  | { readonly kind: "ready" }
  | { readonly kind: "missing"; readonly reason: string }
  | { readonly kind: "unavailable"; readonly reason: string }
  | { readonly kind: "loading" };

const FORWARD_OFFSET_RAD = Math.PI / 2;

/** Ground still lit when it faces away from the sun (`terrain.ts`'s own
 *  `SLOPE_AMBIENT`) -- the same floor here so a creature standing in a
 *  hillside's own shadow does not go a darker black than the hillside does. */
const AMBIENT_FLOOR = 0.6;

const CHARACTER_EMISSIVE: readonly [number, number, number] = [
  0.321569, 0.321569, 0.411765,
];

let characterLight: {
  diffuse: readonly [number, number, number];
  emissive: readonly [number, number, number];
} = { diffuse: [1, 0.972549, 0.972549], emissive: CHARACTER_EMISSIVE };

/** Take the map's record. `Material.Diffuse * DirLights[CHARACTER].Diffuse` is
 *  what the fixed-function pipeline multiplies `N.L` by, and
 *  `Material.Emissive` is the term it adds after
 *  (`MapManager.cpp:237`, `EterLib/StateManager.cpp:190`). */
export function setCreatureLight(environment: MapEnvironment): void {
  const light = environment.directionalLight.character;
  const { diffuse, emissive } = environment.material;
  characterLight = {
    diffuse: [
      diffuse[0] * light.diffuse[0],
      diffuse[1] * light.diffuse[1],
      diffuse[2] * light.diffuse[2],
    ],
    emissive: [emissive[0], emissive[1], emissive[2]],
  };
}

/** `CActorInstance::OnRender`'s material colour, the actor's `SetMaterialColor`
 *  (`ActorInstanceRender.cpp:18-38`), multiplied into the character light's
 *  diffuse channel by channel as the fixed-function pipeline does
 *  (`EterLib/StateManager.cpp:190`). */
export function shadedDiffuse(
  light: readonly [number, number, number],
  shade: readonly [number, number, number],
): [number, number, number] {
  return [light[0] * shade[0], light[1] * shade[1], light[2] * shade[2]];
}

const MAX_STEP_MM = 1_000;

/** Below this, a creature did not move this frame. Not zero: an interpolated
 *  remote entity that has stopped still jitters by fractions of a millimetre
 *  as its snapshots reconcile, and a phase that creeps forward on that reads
 *  as a standing creature slowly pedalling. */
const MOVE_EPSILON_MM = 0.5;

const REST_AFTER_MS = 200;

/** Time constant of the low pass on measured ground speed. Long enough to
 *  bridge the 50-100 ms between snapshots of a remote entity, short enough
 *  that a creature breaking into a run is in its run clip before it has
 *  covered a stride. */
const SPEED_TAU_MS = 180;

const CLIP_FADE_MS = 100; // fBlendTime = 0.1f, ActorInstance.h:299-303

const FALLBACK_STRIDE_MM = 900;

/** int16 per stored bone frame in a `rigid` pack: four of quaternion, three
 *  of translation. `tools/build_creature_meshes.py`'s `_encode_clip` writes it
 *  and its docstring says why seven numbers rather than a 3x4 matrix's
 *  twelve. */
const VALUES_PER_BONE = 7;

/** …and in an `affine` one: the same four, then six of a symmetric 3x3, then
 *  the three. See `SkinEntry.encoding`. */
const AFFINE_VALUES_PER_BONE = 13;

/** How wide this model's bone records are. Exported because every buffer
 *  handed to `sampleClipInto`, `blendPoseInto` and `poseToBoneTexels` has to
 *  agree with it, and one function answering it is one place to be wrong. */
export function valuesPerBone(skin: Pick<SkinEntry, "encoding">): number {
  return skin.encoding === "affine" ? AFFINE_VALUES_PER_BONE : VALUES_PER_BONE;
}

export function zeroPoseTranslation(pose: Float32Array, bones: number, width: number): void {
  const t = width - 3;
  for (let bone = 0; bone < bones; bone++) {
    const o = bone * width + t;
    pose[o] = 0;
    pose[o + 1] = 0;
    pose[o + 2] = 0;
  }
}

const SAME_BONE_MM = 25;
const SAME_BONE_DOT = 1e-3;

export function bodySlotsForHair(
  body: SkinEntry, bodyFrames: Int16Array, hair: SkinEntry, hairFrames: Int16Array,
): Int16Array {
  const slots = new Int16Array(hair.boneCount).fill(-1);
  const hairIdle = hair.clips.idle;
  const bodyIdle = body.clips.idle;
  if (hairIdle === undefined || bodyIdle === undefined) return slots;
  const hairWidth = valuesPerBone(hair);
  const bodyWidth = valuesPerBone(body);
  const hairPose = new Float32Array(hair.boneCount * hairWidth);
  const bodyPose = new Float32Array(body.boneCount * bodyWidth);
  const frames = Math.min(hairIdle.frames, bodyIdle.frames);
  // Worst-frame distance per (hair bone, body slot), built one frame at a
  // time so neither whole clip has to be held in memory at once.
  const worstMm = new Float64Array(hair.boneCount * body.boneCount);
  const rejected = new Uint8Array(hair.boneCount * body.boneCount);
  for (let frame = 0; frame < frames; frame++) {
    // A looping clip's phase f/frames lands exactly on stored frame f, with
    // no blend -- see `sampleClipInto`.
    sampleClipInto(hairPose, hairFrames, hairIdle, hair.boneCount, frame / hairIdle.frames, hairWidth);
    sampleClipInto(bodyPose, bodyFrames, bodyIdle, body.boneCount, frame / bodyIdle.frames, bodyWidth);
    for (let bone = 0; bone < hair.boneCount; bone++) {
      const h = bone * hairWidth;
      for (let slot = 0; slot < body.boneCount; slot++) {
        const at = bone * body.boneCount + slot;
        if (rejected[at] === 1) continue;
        const b = slot * bodyWidth;
        const dot = hairPose[h]! * bodyPose[b]! + hairPose[h + 1]! * bodyPose[b + 1]!
          + hairPose[h + 2]! * bodyPose[b + 2]! + hairPose[h + 3]! * bodyPose[b + 3]!;
        if (1 - Math.abs(dot) > SAME_BONE_DOT) { rejected[at] = 1; continue; }
        const away = Math.hypot(
          hairPose[h + hairWidth - 3]! - bodyPose[b + bodyWidth - 3]!,
          hairPose[h + hairWidth - 2]! - bodyPose[b + bodyWidth - 2]!,
          hairPose[h + hairWidth - 1]! - bodyPose[b + bodyWidth - 1]!,
        );
        if (away > SAME_BONE_MM) { rejected[at] = 1; continue; }
        if (away > worstMm[at]!) worstMm[at] = away;
      }
    }
  }
  for (let bone = 0; bone < hair.boneCount; bone++) {
    let best = Infinity;
    for (let slot = 0; slot < body.boneCount; slot++) {
      const at = bone * body.boneCount + slot;
      if (rejected[at] === 1 || worstMm[at]! >= best) continue;
      best = worstMm[at]!;
      slots[bone] = slot;
    }
  }
  return slots;
}

/** Copy one sampled bone (quaternion + translation) between pose buffers. */
function copyPoseBone(
  src: Float32Array, srcSlot: number, srcWidth: number,
  dst: Float32Array, dstSlot: number, dstWidth: number,
): void {
  const s = srcSlot * srcWidth;
  const d = dstSlot * dstWidth;
  const qx = src[s]!, qy = src[s + 1]!, qz = src[s + 2]!, qw = src[s + 3]!;
  const tx = src[s + srcWidth - 3]!, ty = src[s + srcWidth - 2]!, tz = src[s + srcWidth - 1]!;
  dst[d] = qx; dst[d + 1] = qy; dst[d + 2] = qz; dst[d + 3] = qw;
  if (dstWidth === AFFINE_VALUES_PER_BONE) {
    if (srcWidth === dstWidth) {
      for (let i = 4; i < dstWidth - 3; i++) dst[d + i] = src[s + i]!;
    } else {
      dst[d + 4] = 1; dst[d + 5] = 0; dst[d + 6] = 0;
      dst[d + 7] = 1; dst[d + 8] = 0; dst[d + 9] = 1;
    }
  }
  dst[d + dstWidth - 3] = tx;
  dst[d + dstWidth - 2] = ty;
  dst[d + dstWidth - 1] = tz;
}

/** A quaternion component is stored over this; see the build tool's
 *  `QUAT_QUANT`. */
const QUAT_QUANT = 32767;

const VERTEX_SPEC: readonly AttributeSpec[] = [
  { name: "a_position", components: 3, type: 0x1406 /* FLOAT */, normalized: false, offset: 0, divisor: 0 },
  { name: "a_normal", components: 3, type: 0x1406, normalized: false, offset: 12, divisor: 0 },
  { name: "a_uv", components: 2, type: 0x1406, normalized: false, offset: 24, divisor: 0 },
  // Slots arrive as floats 0..255 (not normalized) because they are indices;
  // weights arrive normalized to 0..1 because they are fractions. Getting
  // that backwards compiles, links and draws a creature turned inside out.
  { name: "a_boneSlot", components: 4, type: 0x1401 /* UNSIGNED_BYTE */, normalized: false, offset: 32, divisor: 0 },
  { name: "a_boneWeight", components: 4, type: 0x1401, normalized: true, offset: 36, divisor: 0 },
];

const VERTEX_SHADER = `#version 300 es
uniform mat4 u_modelViewProj;
uniform mat3 u_normalMatrix;
uniform float u_skinned;
// One row of three RGBA texels per bone: the row-vector 3x3 in .xyz and the
// translation in .w. A texture rather than a uniform array because the
// tallest skeleton in this pack binds 103 bones, which is 309 vec4 against
// the 256 WebGL2 is only obliged to give a vertex shader -- a limit real
// hardware usually exceeds and this pack has no business betting on.
uniform highp sampler2D u_bones;
in vec3 a_position;
in vec3 a_normal;
in vec2 a_uv;
in vec4 a_boneSlot;
in vec4 a_boneWeight;
out vec3 v_normal;
out vec2 v_uv;

void main() {
  vec3 position = a_position;
  vec3 normal = a_normal;
  if (u_skinned > 0.5) {
    vec3 blended = vec3(0.0);
    vec3 blendedNormal = vec3(0.0);
    float total = 0.0;
    for (int i = 0; i < 4; i++) {
      float weight = a_boneWeight[i];
      if (weight <= 0.0) continue;
      int bone = int(a_boneSlot[i] + 0.5);
      vec4 r0 = texelFetch(u_bones, ivec2(0, bone), 0);
      vec4 r1 = texelFetch(u_bones, ivec2(1, bone), 0);
      vec4 r2 = texelFetch(u_bones, ivec2(2, bone), 0);
      blended += weight * (a_position.x * r0.xyz + a_position.y * r1.xyz
                           + a_position.z * r2.xyz + vec3(r0.w, r1.w, r2.w));
      // The same 3x3 for the normal: these matrices are rotations (the build
      // tool refuses a clip whose bones depart from one by more than 1e-3),
      // and the inverse transpose of a rotation is itself.
      blendedNormal += weight * (a_normal.x * r0.xyz + a_normal.y * r1.xyz + a_normal.z * r2.xyz);
      total += weight;
    }
    // A vertex with no influence at all keeps its bind position rather than
    // collapsing to the origin -- the same answer tools/gr2/skin.py's
    // skin_vertex gives for the same case.
    if (total > 0.0) {
      position = blended / total;
      normal = blendedNormal;
    }
  }
  v_normal = u_normalMatrix * normal;
  v_uv = a_uv;
  gl_Position = u_modelViewProj * vec4(position, 1.0);
}
`;

const FRAGMENT_SHADER = `#version 300 es
precision mediump float;
uniform sampler2D u_texture;
uniform float u_hasTexture;
uniform vec3 u_tint;
// DirLights[ENV_DIRLIGHT_CHARACTER].Diffuse * Material.Diffuse and
// Material.Emissive, out of the map's own record -- see characterLight.
uniform vec3 u_lightDiffuse;
uniform vec3 u_lightEmissive;
// CActorInstance's m_fAlphaValue, the one the blend render mode hands the
// device (ActorInstanceBlend.cpp:14-22, ActorInstanceRender.cpp:169-184).
uniform float u_alpha;
// MOB_COLOR, the texture factor RENDER_MODE_MODULATE multiplies the whole lit
// body by (ActorInstanceRender.cpp:253-270); white for every other body.
uniform vec3 u_modulate;
// One for a cast into the character shadow target: the whole colour is
// TFACTOR 0xFF808080 (MapOutdoorCharacterShadow.cpp:103-106).
uniform float u_flat;
in vec3 v_normal;
in vec2 v_uv;
out vec4 fragColour;
const vec3 SUN_DIR = ${SUN_DIR_GLSL};
const float AMBIENT_FLOOR = ${AMBIENT_FLOOR.toFixed(2)};
void main() {
  if (u_flat > 0.5) {
    fragColour = vec4(vec3(${CHARACTER_SHADOW_TEXTURE_FACTOR.toFixed(6)}), 1.0);
    return;
  }
  vec3 n = normalize(v_normal);
  vec3 lit = u_lightDiffuse
    * (AMBIENT_FLOOR + (1.0 - AMBIENT_FLOOR) * max(dot(n, SUN_DIR), 0.0));
  vec3 base = mix(u_tint, texture(u_texture, v_uv).rgb, u_hasTexture);
  // Lit, then lifted. See CHARACTER_EMISSIVE's own comment: a wolf's skin is
  // dark brown before anything shades it, and multiplying a dark texture by a
  // light factor only ever gets darker.
  fragColour = vec4((base * lit + u_lightEmissive * base) * u_modulate, u_alpha);
}
`;

export function creatureModelMatrix(
  xMm: number,
  yMm: number,
  zMm: number,
  headingSteps: number,
  out: Mat4 = new Float32Array(16),
): Mat4 {
  const angle = (headingSteps / HEADING_STEPS) * Math.PI * 2 + FORWARD_OFFSET_RAD;
  const c = Math.cos(angle);
  const s = Math.sin(angle);
  out[0] = c; out[1] = s; out[2] = 0; out[3] = 0;
  out[4] = -s; out[5] = c; out[6] = 0; out[7] = 0;
  out[8] = 0; out[9] = 0; out[10] = 1; out[11] = 0;
  out[12] = xMm; out[13] = yMm; out[14] = zMm; out[15] = 1;
  return out;
}

const MAX_ATTACK_SPEED_RATIO = 4;

export function swingPlaybackRatio(
  authoredGapMs: number | null, intervalMs: number,
): number {
  if (authoredGapMs === null || !(authoredGapMs > 0) || !(intervalMs > 0)) return 1;
  return Math.min(MAX_ATTACK_SPEED_RATIO, Math.max(1, authoredGapMs / intervalMs));
}

/** How far one cycle of this clip carries the creature, in millimetres --
 *  the divisor that turns distance travelled into a phase. See
 *  `FALLBACK_STRIDE_MM` for the clips whose own files do not say. */
export function strideOf(clip: ClipEntry): number {
  return clip.strideMm > 0 ? clip.strideMm : FALLBACK_STRIDE_MM;
}

/** The speed, in mm/s, this clip was authored to move at: how far one cycle
 *  carries the creature over how long the cycle lasts. Used only to decide
 *  which of walk and run a measured ground speed is closer to. */
export function impliedSpeedMmS(clip: ClipEntry): number {
  return clip.durationS > 0 ? strideOf(clip) / clip.durationS : 0;
}

export function selectLocomotion(
  speedMmS: number,
  current: ClipName,
  clips: SkinEntry["clips"],
): ClipName | null {
  const walk = clips.walk;
  const run = clips.run;
  if (walk === undefined) return run === undefined ? null : "run";
  if (run === undefined) return "walk";
  const threshold = Math.sqrt(impliedSpeedMmS(walk) * impliedSpeedMmS(run)) || 0;
  if (threshold <= 0) return "walk";
  if (current === "run") return speedMmS < threshold * 0.85 ? "walk" : "run";
  return speedMmS > threshold * 1.15 ? "run" : "walk";
}

export function sampleClipInto(
  out: Float32Array,
  data: Int16Array,
  clip: ClipEntry,
  bones: number,
  phase: number,
  width: number = VALUES_PER_BONE,
): void {
  const frames = clip.frames;
  const looping = clip.loop !== false;
  const cycles = looping
    ? phase - Math.floor(phase)
    : phase <= 0 ? 0 : phase >= 1 ? 1 : phase;
  const position = cycles * (looping ? frames : frames - 1);
  const first = Math.min(Math.floor(position), frames - 1);
  const second = looping ? (first + 1) % frames : Math.min(first + 1, frames - 1);
  const blend = position - first;
  const stride = bones * width;
  const base = clip.offset / 2;
  const scale = clip.positionScale;
  // A symmetric 3x3 sits between the quaternion and the translation in an
  // `affine` record, so the translation is always the last three -- see
  // `SkinEntry.encoding`.
  const shear = width - VALUES_PER_BONE;
  const shearScale = clip.shearScale ?? 1;
  let a = base + first * stride;
  let b = base + second * stride;
  for (let bone = 0; bone < bones; bone++, a += width, b += width) {
    let bx = data[b] / QUAT_QUANT;
    let by = data[b + 1] / QUAT_QUANT;
    let bz = data[b + 2] / QUAT_QUANT;
    let bw = data[b + 3] / QUAT_QUANT;
    const ax = data[a] / QUAT_QUANT;
    const ay = data[a + 1] / QUAT_QUANT;
    const az = data[a + 2] / QUAT_QUANT;
    const aw = data[a + 3] / QUAT_QUANT;
    if (ax * bx + ay * by + az * bz + aw * bw < 0) {
      bx = -bx; by = -by; bz = -bz; bw = -bw;
    }
    const o = bone * width;
    out[o] = ax + (bx - ax) * blend;
    out[o + 1] = ay + (by - ay) * blend;
    out[o + 2] = az + (bz - az) * blend;
    out[o + 3] = aw + (bw - aw) * blend;
    // A symmetric matrix has no orthogonality to lose, so a plain lerp of
    // these six is exactly right -- which is the reason the 3x3 was split off
    // the rotation in the first place.
    for (let i = 0; i < shear; i++) {
      out[o + 4 + i] = (data[a + 4 + i] + (data[b + 4 + i] - data[a + 4 + i]) * blend) * shearScale;
    }
    const t = 4 + shear;
    out[o + t] = (data[a + t] + (data[b + t] - data[a + t]) * blend) * scale;
    out[o + t + 1] = (data[a + t + 1] + (data[b + t + 1] - data[a + t + 1]) * blend) * scale;
    out[o + t + 2] = (data[a + t + 2] + (data[b + t + 2] - data[a + t + 2]) * blend) * scale;
  }
}

/** Blend one sampled pose towards another, in place. Used only for the
 *  crossfade between clips, the original's control ease-out and ease-in
 *  (`ModelInstanceMotion.cpp:67,101`); the same normalised lerp
 *  `sampleClipInto` uses between frames, and the same hemisphere check for the
 *  same reason. A bone one clip reflects and the other does not has no
 *  rotation between the two, so it stays on `target`'s clip. */
export function blendPoseInto(
  target: Float32Array,
  other: Float32Array,
  weight: number,
  bones: number,
  width: number = VALUES_PER_BONE,
  targetMirrored: Int8Array | null = null,
  otherMirrored: Int8Array | null = null,
): void {
  for (let bone = 0; bone < bones; bone++) {
    if ((targetMirrored?.[bone] ?? 0) !== (otherMirrored?.[bone] ?? 0)) continue;
    const o = bone * width;
    let bx = other[o], by = other[o + 1], bz = other[o + 2], bw = other[o + 3];
    if (target[o] * bx + target[o + 1] * by + target[o + 2] * bz + target[o + 3] * bw < 0) {
      bx = -bx; by = -by; bz = -bz; bw = -bw;
    }
    target[o] += (bx - target[o]) * weight;
    target[o + 1] += (by - target[o + 1]) * weight;
    target[o + 2] += (bz - target[o + 2]) * weight;
    target[o + 3] += (bw - target[o + 3]) * weight;
    // Everything after the quaternion lerps plainly: six symmetric entries
    // (only in an `affine` record) and three millimetres of translation.
    for (let i = 4; i < width; i++) {
      target[o + i] += (other[o + i] - target[o + i]) * weight;
    }
  }
}

export function poseToBoneTexels(
  pose: Float32Array,
  out: Float32Array,
  bones: number,
  mirrored: Int8Array | null = null,
  width: number = VALUES_PER_BONE,
): void {
  const shear = width - VALUES_PER_BONE;
  for (let bone = 0; bone < bones; bone++) {
    const sign = mirrored !== null && mirrored[bone] !== 0 ? -1 : 1;
    const o = bone * width;
    let x = pose[o], y = pose[o + 1], z = pose[o + 2], w = pose[o + 3];
    const length = Math.hypot(x, y, z, w);
    if (length > 0) { x /= length; y /= length; z /= length; w /= length; }
    const xx = x * x, yy = y * y, zz = z * z;
    const xy = x * y, xz = x * z, yz = y * z;
    const wx = w * x, wy = w * y, wz = w * z;
    let r0 = 1 - 2 * (yy + zz), r1 = 2 * (xy + wz), r2 = 2 * (xz - wy);
    let r3 = 2 * (xy - wz), r4 = 1 - 2 * (xx + zz), r5 = 2 * (yz + wx);
    let r6 = 2 * (xz + wy), r7 = 2 * (yz - wx), r8 = 1 - 2 * (xx + yy);
    if (shear > 0) {
      // `M = R * S`, the factorisation `decompose_affine` stored: the six
      // numbers are S's upper triangle in row order and S is symmetric, so
      // s01 is also s10.
      const s0 = pose[o + 4], s1 = pose[o + 5], s2 = pose[o + 6];
      const s3 = pose[o + 7], s4 = pose[o + 8], s5 = pose[o + 9];
      const m0 = r0 * s0 + r1 * s1 + r2 * s2;
      const m1 = r0 * s1 + r1 * s3 + r2 * s4;
      const m2 = r0 * s2 + r1 * s4 + r2 * s5;
      const m3 = r3 * s0 + r4 * s1 + r5 * s2;
      const m4 = r3 * s1 + r4 * s3 + r5 * s4;
      const m5 = r3 * s2 + r4 * s4 + r5 * s5;
      const m6 = r6 * s0 + r7 * s1 + r8 * s2;
      const m7 = r6 * s1 + r7 * s3 + r8 * s4;
      const m8 = r6 * s2 + r7 * s4 + r8 * s5;
      r0 = m0; r1 = m1; r2 = m2; r3 = m3; r4 = m4; r5 = m5; r6 = m6; r7 = m7; r8 = m8;
    }
    const p = o + width - 3;
    const t = bone * 12;
    out[t] = sign * r0;
    out[t + 1] = sign * r1;
    out[t + 2] = sign * r2;
    out[t + 3] = pose[p];
    out[t + 4] = sign * r3;
    out[t + 5] = sign * r4;
    out[t + 6] = sign * r5;
    out[t + 7] = pose[p + 1];
    out[t + 8] = sign * r6;
    out[t + 9] = sign * r7;
    out[t + 10] = sign * r8;
    out[t + 11] = pose[p + 2];
  }
}

export function attachedModelMatrix(
  texels: Float32Array,
  slot: number,
  bindWorld: Mat4,
  out: Mat4 = new Float32Array(16),
  scratch: Mat4 = new Float32Array(16),
): Mat4 {
  const t = slot * 12;
  // Column j of the column-vector form is row j of the row-vector one.
  scratch[0] = texels[t]; scratch[1] = texels[t + 1]; scratch[2] = texels[t + 2]; scratch[3] = 0;
  scratch[4] = texels[t + 4]; scratch[5] = texels[t + 5]; scratch[6] = texels[t + 6]; scratch[7] = 0;
  scratch[8] = texels[t + 8]; scratch[9] = texels[t + 9]; scratch[10] = texels[t + 10]; scratch[11] = 0;
  scratch[12] = texels[t + 3]; scratch[13] = texels[t + 7]; scratch[14] = texels[t + 11]; scratch[15] = 1;
  return multiply(scratch, bindWorld, out);
}

/** The model matrix a rider draws with: `__AttachHorseSaddle` hangs the
 *  rider's PART_MAIN off the horse's `saddle` bone (`InstanceBase.cpp:247-252`),
 *  so the rider's skeleton composes on that bone's animated world transform
 *  (`ModelInstanceUpdate.cpp:146-148`), which composes on the horse's own model
 *  matrix. `texels` is the horse's pose, `slot` and `bindWorld` its saddle row. */
export function saddleModelMatrix(
  horseModel: Mat4,
  texels: Float32Array,
  slot: number,
  bindWorld: Mat4,
  out: Mat4 = new Float32Array(16),
  bone: Mat4 = new Float32Array(16),
  scratch: Mat4 = new Float32Array(16),
): Mat4 {
  return multiply(horseModel, attachedModelMatrix(texels, slot, bindWorld, bone, scratch), out);
}

/** One creature's animation state, carried between frames and keyed by the
 *  entity id the simulation already gave it. */
interface Instance {
  xMm: number;
  yMm: number;
  /** The locomotion clock, in millimetres of ground covered. The only thing a
   *  walk or run phase is ever derived from, and advanced by
   *  `speedMmS * frameDelta` rather than by the step the drawn position took
   *  -- see `advance`. */
  distanceMm: number;
  speedMmS: number;
  /** Wall clock, for the idle cycle and for the crossfade only -- never for
   *  the locomotion phase. */
  idleOffsetS: number;
  clip: ClipName;
  /** Which of the three general loops `clip` stands for, and the mode that
   *  picked its clip -- see `modeLoopClip`. */
  loop: LoopName;
  mode: MotionMode;
  /** The one-shot playing over the loop, if any: what it is and when it
   *  started/ends. `playOnce` records the body clip's authoritative deadline;
   *  an attached part may use a shorter alias but cannot end the body's
   *  animation when it reaches the alias's last frame. */
  once: ClipName | null;
  /** The one-shot `once` hands over to when it ends, as `PushOnceMotion`
   *  queues a stand-up behind a knockdown (`ActorInstanceBattle.cpp:863-882`). */
  onceThen: ClipName | null;
  onceStartedMs: number;
  onceEndsMs: number;
  onceSpeedRatio: number;
  fadeFrom: ClipName;
  fadeFromPhase: number;
  fadeUntilMs: number;
  /** Which file of `once`, `clip` and `fadeFrom` plays: 0 for the first, `i`
   *  for `name~i` (`pickVariant`). */
  onceVariant: number;
  clipVariant: number;
  fadeFromVariant: number;
  /** When the current pass of a standing loop with variants began; NaN until
   *  one has. */
  loopStartedMs: number;
  movingUntilMs: number;
  frame: number;
  /** The skin whose clips this body plays, once it has been drawn or played as one. */
  key: string | null;
}

/** `SetRace` for one body: `CGraphicThingInstance::Clear()` drops the motion it
 *  was playing and the new race's motions are registered in its place
 *  (`ActorInstanceData.cpp:82,97-119`), and the body stands in its wait again
 *  (`RefreshState(NAME_WAIT)`, `InstanceBase.cpp:2815`). */
function clearMotion(instance: Instance): void {
  instance.clip = "idle";
  instance.loop = "idle";
  instance.once = null;
  instance.onceThen = null;
  instance.onceStartedMs = 0;
  instance.onceEndsMs = 0;
  instance.onceSpeedRatio = 1;
  instance.fadeFrom = "idle";
  instance.fadeFromPhase = 0;
  instance.fadeUntilMs = 0;
  instance.onceVariant = 0;
  instance.clipVariant = 0;
  instance.fadeFromVariant = 0;
  instance.loopStartedMs = Number.NaN;
}

export class Instances {
  private readonly byId = new Map<number, Instance>();
  private frame = 0;

  /** Start a frame. Anything not claimed during the *previous* frame is
   *  dropped here rather than accumulating -- interest management means
   *  entities come and go constantly and a table that only grows is a leak
   *  measured in hours, the same reason `creature.ts`'s `GaitTracker` prunes. */
  beginFrame(): void {
    this.frame++;
    for (const [id, instance] of this.byId) {
      if (instance.frame < this.frame - 2) this.byId.delete(id);
    }
  }

  /** The state for this entity, creating it the first time it is seen. Does
   *  not touch it otherwise -- `advance` is what moves a creature, and
   *  `playOnce` needs an entry for an entity it is not drawing this instant --
   *  except that a body asked for under another skin `key` than it last played
   *  from takes that skin's motions from a standing start (`clearMotion`). */
  get(entityId: number, xMm: number, yMm: number, key: string | null = null): Instance {
    const existing = this.byId.get(entityId);
    if (existing !== undefined) {
      if (key !== null && existing.key !== null && existing.key !== key) clearMotion(existing);
      if (key !== null) existing.key = key;
      return existing;
    }
    const mixed = Math.imul(Math.round(xMm) | 0, 0x27d4eb2d) ^ Math.imul(Math.round(yMm) | 0, 0x165667b1);
    const instance: Instance = {
      xMm, yMm,
      distanceMm: 0,
      speedMmS: 0,
      idleOffsetS: (((mixed ^ (mixed >>> 15)) >>> 0) % 997) / 99.7,
      clip: "idle",
      loop: "idle",
      mode: "general",
      once: null,
      onceThen: null,
      onceStartedMs: 0,
      onceEndsMs: 0,
      onceSpeedRatio: 1,
      fadeFrom: "idle",
      fadeFromPhase: 0,
      fadeUntilMs: 0,
      onceVariant: 0,
      clipVariant: 0,
      fadeFromVariant: 0,
      loopStartedMs: Number.NaN,
      movingUntilMs: 0,
      frame: this.frame,
      key,
    };
    this.byId.set(entityId, instance);
    return instance;
  }

  /** The state for this entity if it has one, without creating it. */
  peek(entityId: number): Instance | undefined {
    return this.byId.get(entityId);
  }

  /** Move one entity to where it is being drawn this frame, adding what it
   *  covered to its path length and folding the implied speed into its
   *  smoothed one. Returns the instance. */
  advance(entityId: number, xMm: number, yMm: number, nowMs: number, frameDeltaMs: number, key: string | null = null): Instance {
    const instance = this.get(entityId, xMm, yMm, key);
    if (instance.frame === this.frame) return instance; // drawn twice in one frame
    const stepMm = Math.hypot(xMm - instance.xMm, yMm - instance.yMm);
    instance.xMm = xMm;
    instance.yMm = yMm;
    instance.frame = this.frame;
    if (stepMm > MAX_STEP_MM) return instance;
    if (stepMm > MOVE_EPSILON_MM) {
      instance.movingUntilMs = nowMs + Math.max(REST_AFTER_MS, frameDeltaMs * 2);
    }
    const instant = frameDeltaMs > 0 ? (stepMm / frameDeltaMs) * 1000 : 0;
    // One-pole low pass with SPEED_TAU_MS, exact for any frame interval
    // rather than a fixed per-frame constant, so a 30 Hz tab and a 144 Hz one
    // decide walk-versus-run the same way.
    const alpha = 1 - Math.exp(-frameDeltaMs / SPEED_TAU_MS);
    instance.speedMmS += (instant - instance.speedMmS) * alpha;
    instance.distanceMm += (instance.speedMmS * frameDeltaMs) / 1000;
    return instance;
  }

  get tracked(): number {
    return this.byId.size;
  }
}

function stack(element: HTMLElement): void {
  const style = element.style;
  style.position = "fixed";
  style.left = "0";
  style.top = "0";
  style.width = "100vw";
  style.height = "100vh";
  style.zIndex = "0";
  style.pointerEvents = "none";
}

/** A half-open `[start, end)` window of bytes in `creaturemesh.bin`. */
export interface ByteRange {
  readonly start: number;
  readonly end: number;
}

export function mergeByteRanges(
  ranges: readonly ByteRange[],
  gap: number = 0,
): ByteRange[] {
  const sorted = ranges
    .filter((range) => range.end > range.start)
    .slice()
    .sort((a, b) => a.start - b.start);
  const merged: ByteRange[] = [];
  for (const range of sorted) {
    const last = merged[merged.length - 1];
    if (last !== undefined && range.start <= last.end + gap) {
      if (range.end > last.end) merged[merged.length - 1] = { start: last.start, end: range.end };
      continue;
    }
    merged.push(range);
  }
  return merged;
}

export function subtractByteRanges(
  wanted: readonly ByteRange[],
  held: readonly ByteRange[],
): ByteRange[] {
  const have = mergeByteRanges(held);
  const missing: ByteRange[] = [];
  for (const range of mergeByteRanges(wanted)) {
    let start = range.start;
    for (const covered of have) {
      if (covered.end <= start) continue;
      if (covered.start >= range.end) break;
      if (covered.start > start) missing.push({ start, end: covered.start });
      start = Math.max(start, covered.end);
      if (start >= range.end) break;
    }
    if (start < range.end) missing.push({ start, end: range.end });
  }
  return missing;
}

/** Every byte `key`'s submeshes occupy, unmerged and in index order. Empty for
 *  a model this pack does not carry, which is exactly the case `status()`
 *  already reports as `missing`. */
export function byteRangesOf(index: CreatureIndex, key: string): ByteRange[] {
  const model = index.models[key];
  if (model === undefined) return [];
  const stride = index.vertexStride;
  const ranges: ByteRange[] = [];
  for (const mesh of model.meshes) {
    const vertexBytes = mesh.vertexByteLength ?? (mesh.vertexCount ?? 0) * stride;
    const indexBytes = mesh.indexByteLength
      ?? mesh.indexCount * (mesh.indexType === "uint16" ? 2 : 4);
    ranges.push({ start: mesh.vertexOffset, end: mesh.vertexOffset + vertexBytes });
    ranges.push({ start: mesh.indexOffset, end: mesh.indexOffset + indexBytes });
  }
  return ranges;
}

export function packByteLength(index: CreatureIndex): number {
  let end = 0;
  for (const key of Object.keys(index.models)) {
    for (const range of byteRangesOf(index, key)) end = Math.max(end, range.end);
  }
  return end;
}

export const RANGE_COALESCE_BYTES = 65536;

/** How `require` reports on one model's geometry. */
export type MeshResidency = "resident" | "loading" | "failed";

export interface CreaturePack {
  readonly index: CreatureIndex;
  readonly blob: ArrayBuffer;
  require(key: string): MeshResidency;
  /** Why `key`'s geometry will never arrive, for a key `require` has already
   *  reported as `failed`. Null for every other key. Kept so that the
   *  silhouette a player ends up looking at can say what went wrong, rather
   *  than leaving the reason in a console line nobody kept. */
  failureFor(key: string): string | null;
  /** The windows that have landed, in arrival order and never rewritten. A
   *  renderer uploads the ones it has not seen yet and remembers the count;
   *  see `syncBuffers`. */
  readonly segments: readonly ByteRange[];
}

let sharedPack: Promise<CreaturePack> | null = null;

class WindowedCreaturePack implements CreaturePack {
  readonly blob: ArrayBuffer;
  readonly segments: ByteRange[] = [];
  private readonly bytes: Uint8Array;
  /** Merged and sorted; the invariant every subtraction below relies on. */
  private held: ByteRange[] = [];
  private readonly inFlight: Array<{ range: ByteRange; done: Promise<void> }> = [];
  /** Models asked for since the last flush. Batched because `status()` is
   *  called many times in one frame and one frame's worth of wants coalesce
   *  into far fewer requests than one want each would. */
  private readonly wanted = new Set<string>();
  private flushing = false;
  private readonly failed = new Map<string, string>();

  constructor(readonly index: CreatureIndex, private readonly fetchRange = defaultRangeFetch) {
    this.blob = new ArrayBuffer(packByteLength(index));
    this.bytes = new Uint8Array(this.blob);
  }

  failureFor(key: string): string | null {
    return this.failed.get(key) ?? null;
  }

  require(key: string): MeshResidency {
    if (this.failed.has(key)) return "failed";
    const ranges = byteRangesOf(this.index, key);
    // A model with no bytes -- one the pack does not carry -- is not this
    // method's problem to report; `status()` has already answered `missing`
    // for it by the time anything calls here, and saying "resident" keeps
    // that answer rather than shadowing it with a second one.
    if (subtractByteRanges(ranges, this.held).length === 0) return "resident";
    this.wanted.add(key);
    this.scheduleFlush();
    return "loading";
  }

  private scheduleFlush(): void {
    if (this.flushing) return;
    this.flushing = true;
    queueMicrotask(() => {
      this.flushing = false;
      void this.flush();
    });
  }

  private async flush(): Promise<void> {
    const keys = [...this.wanted];
    this.wanted.clear();
    if (keys.length === 0) return;

    const wanted: ByteRange[] = [];
    for (const key of keys) wanted.push(...byteRangesOf(this.index, key));

    const outstanding = subtractByteRanges(wanted, this.held);
    // Anything another flush is already fetching, we wait for rather than
    // fetch again. `inFlight` is only ever appended to below and pruned when
    // a request settles, so this is the whole of the deduplication.
    const covered = this.inFlight.map((entry) => entry.range);
    const mine = mergeByteRanges(
      subtractByteRanges(outstanding, covered),
      RANGE_COALESCE_BYTES,
    );

    const waits: Array<Promise<void>> = [];
    for (const entry of this.inFlight) {
      const wantsIt = outstanding.some(
        (range) => range.start < entry.range.end && entry.range.start < range.end,
      );
      if (wantsIt) waits.push(entry.done);
    }
    for (const range of mine) waits.push(this.start(range, keys));
    await Promise.all(waits.map((wait) => wait.catch(() => undefined)));
  }

  /** Fetch one window, register it as in flight for the duration, and record
   *  the keys that were waiting on it so a failure has somewhere to land. */
  private start(range: ByteRange, keys: readonly string[]): Promise<void> {
    const done = (async () => {
      try {
        const landed = await fetchCreaturePackWithRetry(() => this.fetchRange(range));
        this.absorb(landed);
      } catch (error) {
        const reason = String(error);
        // Only the models whose bytes are actually in *this* window. "Is
        // still missing something" would be the wrong test: a model waiting
        // on two windows, one of them another request that has simply not
        // come back yet, is still missing bytes and has failed at nothing.
        for (const key of keys) {
          const inThisWindow = byteRangesOf(this.index, key).some(
            (wanted) => wanted.start < range.end && range.start < wanted.end,
          );
          if (inThisWindow) this.failed.set(key, reason);
        }
        console.warn(`creature geometry ${range.start}-${range.end} unavailable (${reason})`);
        throw error;
      } finally {
        const at = this.inFlight.findIndex((entry) => entry.range === range);
        if (at >= 0) this.inFlight.splice(at, 1);
      }
    })();
    this.inFlight.push({ range, done });
    return done;
  }

  /** Copy a landed window into the blob and publish it as a segment. */
  private absorb(landed: LandedRange): void {
    const start = Math.max(0, Math.min(landed.start, this.bytes.length));
    const body = new Uint8Array(landed.body);
    const length = Math.min(body.length, this.bytes.length - start);
    if (length <= 0) return;
    this.bytes.set(body.subarray(0, length), start);
    const range = { start, end: start + length };
    this.held = mergeByteRanges([...this.held, range]);
    // Appended verbatim rather than merged: a renderer uploads segments it
    // has not seen by index, and rewriting history would make it re-upload
    // bytes it already has or skip bytes it does not.
    this.segments.push(range);
  }
}

/** One window as it came back off the wire: where the server says it starts,
 *  and its bytes. `start` is the server's answer, not the client's question --
 *  a server may narrow a range, and RFC 9110 makes `Content-Range` the
 *  authority on what it actually sent. */
interface LandedRange {
  readonly start: number;
  readonly body: ArrayBuffer;
}

/** `Content-Range: bytes 8-15/64` -> 8. Null for anything else, including the
 *  `bytes * /64` form a 416 carries, which names no bytes at all. */
export function firstByteOfContentRange(value: string | null): number | null {
  if (value === null) return null;
  const match = /^\s*bytes\s+(\d+)-(\d+)\/(?:\d+|\*)\s*$/i.exec(value);
  if (match === null) return null;
  const first = Number(match[1]);
  return Number.isSafeInteger(first) ? first : null;
}

/** Ask the server for one window of `creaturemesh.bin`. */
function defaultRangeFetch(range: ByteRange): Promise<LandedRange> {
  return rangeFetchFrom(BIN_URL, range);
}

/** The same, for any blob in `creaturemesh/` -- the costume half is a second
 *  file served on exactly these terms, so it is one URL, not a second reader. */
async function rangeFetchFrom(url: string, range: ByteRange): Promise<LandedRange> {
  const response = await fetch(url, {
    headers: { Range: `bytes=${range.start}-${range.end - 1}` },
  });
  if (!response.ok) throw new Error(`${url}: HTTP ${response.status}`);
  const body = await response.arrayBuffer();
  if (response.status !== 206) {
    // 200, or a mock that does not model status codes at all: the whole file,
    // starting at zero. Not an error -- see the class docstring.
    return { start: 0, body };
  }
  const stated = firstByteOfContentRange(response.headers?.get?.("Content-Range") ?? null);
  return { start: stated ?? range.start, body };
}

/** The index and geometry blob, shared across every renderer in the document. */
export function loadCreaturePack(): Promise<CreaturePack> {
  if (sharedPack !== null) return sharedPack;
  const request = fetchCreaturePackWithRetry(async (): Promise<CreaturePack> => {
    const response = await fetch(INDEX_URL);
    if (!response.ok) throw new Error(`${INDEX_URL}: HTTP ${response.status}`);
    const index = await response.json() as CreatureIndex;
    return new WindowedCreaturePack(index);
  });
  sharedPack = request;
  request.catch(() => {
    if (sharedPack === request) sharedPack = null;
  });
  return request;
}

const sharedShards = new Map<string, Promise<Int16Array>>();

/** One animation shard (`SkinEntry.anim`), fetched once and shared across
 *  every renderer in the document; a failed fetch is forgotten, so a later
 *  ask fetches again. */
export function loadAnimationShard(file: string): Promise<Int16Array> {
  const cached = sharedShards.get(file);
  if (cached !== undefined) return cached;
  const url = `${BASE_URL}${file}`;
  const request = fetchAnimationWithRetry(() => fetch(url), undefined, url);
  sharedShards.set(file, request);
  request.catch(() => {
    if (sharedShards.get(file) === request) sharedShards.delete(file);
  });
  return request;
}

/** Forget the shared pack. For tests, which give each renderer its own
 *  `fetch` and must not inherit the previous test's pack. */
export function resetCreaturePackCache(): void {
  sharedPack = null;
  sharedShards.clear();
  sharedCostumePack = null;
  resolvedCostumePack = null;
}

// ---------------------------------------------------------------------------
// The costume half
// ---------------------------------------------------------------------------

let sharedCostumePack: Promise<CreaturePack> | null = null;
/** The settled value of `sharedCostumePack`, for the callers that cannot await
 *  -- `bodyModelKey` answers inside a frame, and `syncBuffers` is polled from
 *  `draw`. Null while the fetch is in flight, which is the bare body. */
let resolvedCostumePack: CreaturePack | null = null;

/** A costume index as the builder writes it keeps each hit trail once, in
 *  `hitTrails`, and names it from a clip row by `hitTrail`
 *  (`hoist_hit_trails` in `tools/build_creature_meshes.py`): the index with every
 *  such row carrying its trail as `hitPositions` again, as every reader of a
 *  clip entry expects. */
export function inlineHitTrails(index: CreatureIndex): CreatureIndex {
  const shaped = index as unknown as {
    hitTrails?: readonly unknown[];
    skins?: Record<string, { clips?: Record<string, Record<string, unknown>> }>;
  };
  const trails = shaped.hitTrails;
  if (trails === undefined) return index;
  for (const skin of Object.values(shaped.skins ?? {})) {
    for (const row of Object.values(skin.clips ?? {})) {
      const place = row.hitTrail;
      if (typeof place !== "number") continue;
      row.hitPositions = trails[place];
      delete row.hitTrail;
    }
  }
  return index;
}

export function loadCostumePack(index: CreatureIndex): Promise<CreaturePack> | null {
  const ref = index.costumePack;
  if (ref === undefined) return null;
  if (sharedCostumePack !== null) return sharedCostumePack;
  const indexUrl = `${BASE_URL}${ref.index}`;
  const binUrl = `${BASE_URL}${ref.bin}`;
  const request = fetchCreaturePackWithRetry(async (): Promise<CreaturePack> => {
    const response = await fetch(indexUrl);
    if (!response.ok) throw new Error(`${indexUrl}: HTTP ${response.status}`);
    const costumeIndex = inlineHitTrails(await response.json() as CreatureIndex);
    return new WindowedCreaturePack(costumeIndex, (range) => rangeFetchFrom(binUrl, range));
  });
  sharedCostumePack = request;
  request.then(
    (pack) => {
      if (sharedCostumePack === request) resolvedCostumePack = pack;
    },
    () => {
      if (sharedCostumePack === request) sharedCostumePack = null;
    },
  );
  return request;
}

/** The costume pack if it has landed, else null. Synchronous by design: the
 *  callers are a frame's `bodyModelKey` and `syncBuffers`, neither of which can
 *  await, and null is a complete answer -- the bare body. */
export function costumePackIfReady(): CreaturePack | null {
  return resolvedCostumePack;
}

function elsewhereShapeIn(index: CreatureIndex, vnum: number, race: number): number | null {
  const shape = index.armourShapes?.[String(vnum)];
  const entry = index.races?.[String(race)];
  if (shape === undefined || entry === undefined) return null;
  const jobs = index.armourJobs?.[String(vnum)];
  if (jobs !== undefined && !jobs.includes(entry.job)) return null;
  if (shape === entry.baseShape) return null;
  return index.raceBodies?.[String(race)]?.[String(shape)] === undefined ? shape : null;
}

export function costumeBodyModelKeyIn(
  index: CreatureIndex,
  vnum: number,
  race: number = 0,
): string | null {
  const shape = elsewhereShapeIn(index, vnum, race);
  if (shape === null) return null;
  const pending = loadCostumePack(index);
  if (pending === null) return null;
  const costume = resolvedCostumePack;
  if (costume === null) return null;
  const key = costume.index.raceBodies?.[String(race)]?.[String(shape)];
  return key !== undefined && costume.index.models[key] !== undefined ? key : null;
}

export class CreatureMeshRenderer {
  private readonly sharedContext: boolean;
  private canvas: HTMLCanvasElement | null = null;
  private gl: WebGL2RenderingContext | null = null;
  private program: WebGLProgram | null = null;
  private vao: WebGLVertexArrayObject | null = null;
  private uniforms: Record<
    | "u_modelViewProj" | "u_normalMatrix" | "u_texture" | "u_hasTexture" | "u_tint"
    | "u_skinned" | "u_bones" | "u_lightDiffuse" | "u_lightEmissive" | "u_alpha" | "u_modulate" | "u_flat",
    WebGLUniformLocation | null
  > | null = null;
  private vbo: WebGLBuffer | null = null;
  private ebo: WebGLBuffer | null = null;
  /** The costume pack's own buffer pair. A second pair rather than a bigger
   *  first one: the first is sized `pack.blob.byteLength` at construction, and
   *  the whole point of the split is that that number stays 27.8 MB for a
   *  session that never meets a costume. `packOf` picks between them. */
  private costumeVbo: WebGLBuffer | null = null;
  private costumeEbo: WebGLBuffer | null = null;
  private costumeUploadedSegments = 0;
  private costume: CreaturePack | null = null;
  private boneTexture: WebGLTexture | null = null;
  /** The target `castShadow` draws into, or null for no cast pass. */
  private characterShadow: CharacterShadowMap | null = null;
  private readonly shadowMvp: Mat4 = new Float32Array(16);

  private index: CreatureIndex | null = null;
  /** The shared pack, kept because geometry keeps arriving after the index
   *  does. `index` stays a separate field so that every read of it is
   *  unchanged; this is only ever asked two things -- whether a model's bytes
   *  are here yet, and which windows this renderer has not uploaded. */
  private pack: CreaturePack | null = null;
  private uploadedSegments = 0;
  /** Each animation shard this renderer has asked for, by file: its frames
   *  once landed, "loading" until then. See `framesOf`. */
  private animation = new Map<string, Int16Array | "loading">();
  /** Why a shard never landed, by file. Its skins draw as silhouettes. */
  private animationFailures = new Map<string, string>();
  private loadFailed: string | null = null;
  /** Why this renderer can pose nothing at all. */
  private animationFailed: string | null = null;
  private readonly textures = new Map<string, WebGLTexture | "loading" | "failed">();
  private readonly modelMatrix = identity();
  /** Each actor's material colour by entity id, as the painter last set it. */
  private readonly shades = new Map<number, readonly [number, number, number]>();
  private readonly mvp = identity();
  private readonly normalMatrix = new Float32Array(9);

  private readonly instances = new Instances();
  /** Scratch, sized once from `index.maxBoneCount`: the sampled pose, the
   *  pose being faded out of, and the texels those become. Reused for every
   *  creature in every frame -- a per-creature allocation here would be one
   *  garbage collection pause per frame at the frame rates this runs at. */
  private pose: Float32Array = new Float32Array(0);
  private fadePose: Float32Array = new Float32Array(0);
  /** The pose of an attached part -- `PART_HAIR` -- while `pose` still holds
   *  the body palette it is being copied out of. */
  private partPose: Float32Array = new Float32Array(0);
  private texels: Float32Array = new Float32Array(0);
  private lastFrameMs = 0;
  private frameDeltaMs = 16;
  /** `clipMirroredBones` as a per-slot flag array, built once per model (and
   *  once per clip that carries its own list) the first time it draws --
   *  `poseToBoneTexels` runs per bone per creature per frame and has no
   *  business searching a list in there. */
  private readonly mirroredBones = new Map<string, Int8Array | null>();
  /** `bodySlotsForHair` per body+part key, filled the first time that pair
   *  draws. */
  private partSlots = new Map<string, Int16Array>();
  /** `attach.bindWorldMm` as a `Mat4`, built once per model+part the first
   *  time something is hung there -- `index.json` hands it over as a plain
   *  JSON array and `uniformMatrix4fv` wants a typed one. */
  private readonly bindWorlds = new Map<string, Mat4>();
  private readonly attachMatrix = identity();
  private heldOffsetMm: number | null = null;
  /** Each seated rider's model matrix, by rider entity id -- see `seatRider`. */
  private readonly seats = new Map<number, Mat4>();
  /** The last drawn body's saddle bone as a rider's model matrix; null when
   *  that body has no saddle or drew no skeleton. */
  private lastSaddle: Mat4 | null = null;
  private readonly saddleWorld = identity();
  private readonly saddleBone = identity();
  private readonly saddleScratch = identity();
  /** The last drawn body's weapon attach bone, world millimetres -- see
   *  `drawHeld`. Null when that body held nothing. */
  private lastWeaponAnchor: { xMm: number; yMm: number; zMm: number } | null = null;
  private lastWeaponTip: { xMm: number; yMm: number; zMm: number } | null = null;
  private lastWeaponTipLeft: { xMm: number; yMm: number; zMm: number } | null = null;
  private lastWeaponAnchorLeft: { xMm: number; yMm: number; zMm: number } | null = null;
  private skinnedBones = 0;
  /** Where a motion's variant pick rolls from. */
  private random: () => number = Math.random;
  private readonly attachScratch = identity();
  private readonly attachedMvp = identity();
  private readonly attachedNormal = new Float32Array(9);

  constructor(
    private readonly hostCanvas: HTMLCanvasElement,
    sharedGl: WebGL2RenderingContext | null = null,
    sharedCanvas: HTMLCanvasElement | null = null,
  ) {
    this.sharedContext = sharedGl !== null && sharedCanvas !== null;
    if (!canUseEntityGlLayer(hostCanvas, this.sharedContext) || hostCanvas.parentNode === null) return;

    // Not inserted into the document here -- see `beginFrame`'s own comment
    // on why construction is the wrong moment for that.
    const canvas = sharedCanvas ?? document.createElement("canvas");
    if (sharedCanvas === null) stack(canvas);

    let gl: WebGL2RenderingContext | null = null;
    try {
      gl = resolveCreatureContext(sharedGl, canvas);
      if (gl === null) throw new GlError("webgl2 context unavailable for the creature mesh layer");
      const program = createProgram(gl, VERTEX_SHADER, FRAGMENT_SHADER, "creature mesh");
      const vao = createVertexArray(gl, "creature mesh");
      gl.bindVertexArray(vao);
      gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
      checkGlError(gl, "creature mesh setup");
      this.program = program;
      this.vao = vao;
      this.uniforms = uniformLocations(gl, program, [
        "u_modelViewProj",
        "u_normalMatrix",
        "u_texture",
        "u_hasTexture",
        "u_tint",
        "u_skinned",
        "u_bones",
        "u_lightDiffuse",
        "u_lightEmissive",
        "u_alpha",
        "u_modulate",
        "u_flat",
      ]);
    } catch (error) {
      this.loadFailed = String(error);
      if (sharedGl === null) {
        gl?.getExtension("WEBGL_lose_context")?.loseContext();
        canvas.remove();
      }
      console.warn(`creature meshes unavailable (${String(error)}); drawing silhouettes instead`);
      return;
    }
    this.canvas = canvas;
    this.gl = gl;

    // Shared across every renderer in the document -- see `loadCreaturePack`.
    // Each renderer still uploads its own GPU copy, because each has its own
    // GL context; what is shared is the download and the decode.
    loadCreaturePack()
      .then(async (pack) => {
        const { index } = pack;
        if (index.skins !== undefined && Object.keys(index.skins).length > 0) {
          try {
            this.allocateBones(index);
          } catch (error) {
            // Only the animated models lose out; see `status`.
            this.animationFailed = String(error);
            console.warn(`creature animation unavailable (${this.animationFailed}); animated species fall back to silhouettes`);
          }
        }
        // `pack` before `allocate`, which catches up on any window that has
        // already landed, and `index` last because `status()` reads it as the
        // signal that the pack is ready.
        this.pack = pack;
        this.allocate(pack);
        this.index = index;
      })
      .catch((error) => {
        this.loadFailed = String(error);
        console.warn(`creature meshes unavailable (${this.loadFailed}); drawing silhouettes instead`);
      });
  }

  private allocateBones(index: CreatureIndex): void {
    const gl = this.gl;
    if (gl === null) return;
    const bones = Math.max(1, index.maxBoneCount ?? 0);
    // Sized for the widest record any model in the pack could use rather than
    // for this model's: it is one allocation for the whole session and six
    // floats a bone is not worth a second one.
    this.pose = new Float32Array(bones * AFFINE_VALUES_PER_BONE);
    this.fadePose = new Float32Array(bones * AFFINE_VALUES_PER_BONE);
    this.partPose = new Float32Array(bones * AFFINE_VALUES_PER_BONE);
    this.texels = new Float32Array(bones * 12);
    const texture = gl.createTexture();
    if (texture === null) throw new GlError("could not create the creature bone texture");
    gl.activeTexture(gl.TEXTURE1);
    gl.bindTexture(gl.TEXTURE_2D, texture);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA32F, 3, bones, 0, gl.RGBA, gl.FLOAT, null);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    checkGlError(gl, "creature bone texture");
    this.boneTexture = texture;
  }

  private allocate(pack: CreaturePack): void {
    const gl = this.gl;
    if (gl === null) return;
    const size = pack.blob.byteLength;
    const vbo = createBuffer(gl, "creature mesh vertex");
    gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
    gl.bufferData(gl.ARRAY_BUFFER, size, gl.STATIC_DRAW);
    const ebo = createBuffer(gl, "creature mesh index");
    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, ebo);
    gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, size, gl.STATIC_DRAW);
    checkGlError(gl, "creature mesh upload");
    this.vbo = vbo;
    this.ebo = ebo;
    this.uploadedSegments = 0;
    this.syncBuffers();
  }

  private indexOf(key: string): CreatureIndex | null {
    if (this.index !== null && this.index.models[key] !== undefined) return this.index;
    const costume = this.costume;
    if (costume !== null && costume.index.models[key] !== undefined) return costume.index;
    return null;
  }

  private packOf(key: string): CreaturePack | null {
    if (this.index !== null && this.index.models[key] !== undefined) return this.pack;
    const costume = this.costume;
    if (costume !== null && costume.index.models[key] !== undefined) return costume;
    return null;
  }

  /** The buffer pair `key`'s vertices were uploaded into. Both null until the
   *  pack that holds them has been allocated. */
  private buffersFor(key: string): { vbo: WebGLBuffer | null; ebo: WebGLBuffer | null } {
    if (this.index !== null && this.index.models[key] !== undefined) {
      return { vbo: this.vbo, ebo: this.ebo };
    }
    return { vbo: this.costumeVbo, ebo: this.costumeEbo };
  }

  private syncBuffers(): void {
    const gl = this.gl;
    if (gl === null) return;
    if (this.costume === null) {
      const landed = costumePackIfReady();
      if (landed !== null) this.adoptCostumePack(landed);
    }
    if (this.pack !== null) {
      this.uploadedSegments = this.uploadWindows(
        gl, this.pack, this.vbo, this.ebo, this.uploadedSegments);
    }
    if (this.costume !== null) {
      this.costumeUploadedSegments = this.uploadWindows(
        gl, this.costume, this.costumeVbo, this.costumeEbo, this.costumeUploadedSegments);
    }
  }

  /** One pack's newly landed windows into one buffer pair. Returns how many
   *  segments are now uploaded. */
  private uploadWindows(
    gl: WebGL2RenderingContext,
    pack: CreaturePack,
    vbo: WebGLBuffer | null,
    ebo: WebGLBuffer | null,
    uploaded: number,
  ): number {
    if (uploaded === pack.segments.length) return uploaded;
    if (vbo === null || ebo === null) return uploaded;
    for (const { start, end } of pack.segments.slice(uploaded)) {
      const window = new Uint8Array(pack.blob, start, end - start);
      gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
      gl.bufferSubData(gl.ARRAY_BUFFER, start, window);
      gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, ebo);
      gl.bufferSubData(gl.ELEMENT_ARRAY_BUFFER, start, window);
    }
    checkGlError(gl, "creature mesh window upload");
    return pack.segments.length;
  }

  /** `skin`'s clip frames if its shard has landed, else null. The first ask
   *  for a shard is what fetches it, so a pack's shards arrive only for the
   *  bodies something draws. */
  private framesOf(skin: SkinEntry): Int16Array | null {
    const file = skin.anim;
    const held = this.animation.get(file);
    if (held !== undefined) return held === "loading" ? null : held;
    if (this.animationFailures.has(file)) return null;
    this.animation.set(file, "loading");
    loadAnimationShard(file).then(
      (frames) => {
        this.animation.set(file, frames);
      },
      (error) => {
        this.animation.delete(file);
        this.animationFailures.set(file, String(error));
        console.warn(`creature animation ${file} unavailable (${String(error)}); its bodies fall back to silhouettes`);
      },
    );
    return null;
  }

  /** Reserve the costume blob on the GPU, on exactly `allocate`'s terms and
   *  for exactly its reason: every offset in the costume index is then already
   *  the byte offset its draw call needs. */
  private adoptCostumePack(pack: CreaturePack): void {
    const gl = this.gl;
    if (gl === null) return;
    const wider = pack.index.maxBoneCount ?? 0;
    if (wider > (this.index?.maxBoneCount ?? 0)) {
      if (this.boneTexture !== null) gl.deleteTexture(this.boneTexture);
      this.boneTexture = null;
      this.allocateBones(pack.index);
    }
    const size = pack.blob.byteLength;
    const vbo = createBuffer(gl, "costume mesh vertex");
    gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
    gl.bufferData(gl.ARRAY_BUFFER, size, gl.STATIC_DRAW);
    const ebo = createBuffer(gl, "costume mesh index");
    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, ebo);
    gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, size, gl.STATIC_DRAW);
    checkGlError(gl, "costume mesh upload");
    this.costumeVbo = vbo;
    this.costumeEbo = ebo;
    this.costumeUploadedSegments = 0;
    this.costume = pack;
  }

  /** Whether `key` (a species name, or `PLAYER_MODEL_KEY`) will draw as
   *  geometry this frame, and why not when it will not. */
  heightMm(key: string): number | null {
    const model = this.indexOf(key)?.models[key];
    if (model === undefined) return null;
    let top = Number.NEGATIVE_INFINITY;
    for (const mesh of model.meshes) top = Math.max(top, mesh.bboxMax[2]);
    return Number.isFinite(top) ? top : null;
  }

  /** The model a guild building race draws as; `undefined` while the index
   *  loads, `null` once it names none. */
  buildingModelKey(vnum: number): string | null | undefined {
    if (this.index === null) return undefined;
    const key = this.index.buildingModels?.[String(vnum)];
    return key !== undefined && this.index.models[key] !== undefined ? key : null;
  }

  petModelKey(vnum: number): string | null {
    const key = this.index?.petModels?.[String(vnum)];
    return key !== undefined && this.index?.models[key] !== undefined ? key : null;
  }

  itemModelKey(vnum: number): string | null {
    const key = this.index?.itemModels?.[String(vnum)];
    return key !== undefined && this.index?.models[key] !== undefined ? key : null;
  }

  itemHands(vnum: number): readonly string[] {
    return this.index?.itemHands?.[String(vnum)] ?? [];
  }

  /** Which attach parts a weapon occupies while mounted. Older packs have no
   *  conditional table, so they retain their ordinary hand placement. */
  itemMountedHands(vnum: number): readonly string[] {
    return this.index?.itemMountedHands?.[String(vnum)] ?? this.itemHands(vnum);
  }

  /** Which classes may hold a weapon vnum, out of the proto's anti-flags.
   *  Empty for a vnum this pack carries no model for. */
  itemJobs(vnum: number): readonly string[] {
    return this.index?.itemJobs?.[String(vnum)] ?? [];
  }

  /** Which authored attack the worn weapon calls for. Presentation only: the
   *  vnum came from the authoritative worn snapshot, never an attack packet. */
  weaponAttackClip(vnum: number): ClipName {
    return this.index === null ? "attack" : weaponAttackClipIn(this.index, vnum);
  }

  clipDurationMs(key: string, clip: ClipName): number {
    return this.index === null ? 0 : clipDurationMsIn(this.index, key, clip);
  }

  activeClip(entityId: number): ClipName | null {
    const instance = this.instances.peek(entityId);
    return instance === undefined ? null : instance.once ?? instance.clip;
  }

  /** The last drawn body's held-item offset -- see `heldOffsetMm`. Read
   *  immediately after that body's `draw`, or it is somebody else's. */
  /** Bones the last `draw` skinned with; 0 means it painted bind vertices.
   *  See `skinnedBones`. Read immediately after the draw whose answer is
   *  wanted, exactly as `heldOffset` is. */
  skinnedBoneCount(): number {
    return this.skinnedBones;
  }

  heldOffset(): number | null {
    return this.heldOffsetMm;
  }

  /** The last drawn body's saddle as a rider's model matrix, read straight
   *  after `draw` exactly as `heldOffset` is; null when it has none. */
  saddle(): Mat4 | null {
    return this.lastSaddle;
  }

  /** Draw `riderId`'s body and hair at `seat` from now on, or back on the
   *  ground when `seat` is null. The matrix is copied. */
  seatRider(riderId: number, seat: Mat4 | null): void {
    if (seat === null) {
      this.seats.delete(riderId);
      return;
    }
    const held = this.seats.get(riderId) ?? new Float32Array(16);
    held.set(seat);
    this.seats.set(riderId, held);
  }

  /** The last drawn body's weapon attach bone in world millimetres, read
   *  straight after `draw` exactly as `heldOffset` is; null when it held
   *  nothing or drew no skeleton. */
  weaponAnchor(): { xMm: number; yMm: number; zMm: number } | null {
    return this.lastWeaponAnchor;
  }

  weaponAnchorLeft(): { xMm: number; yMm: number; zMm: number } | null {
    return this.lastWeaponAnchorLeft;
  }

  /** The last drawn body's blade tips in world millimetres, read straight
   *  after `draw` as `weaponAnchor` is; null when it held nothing. */
  weaponTip(): { xMm: number; yMm: number; zMm: number } | null {
    return this.lastWeaponTip;
  }

  weaponTipLeft(): { xMm: number; yMm: number; zMm: number } | null {
    return this.lastWeaponTipLeft;
  }

  /** The last drawn body's `effect:<bone>` attach bone in world millimetres,
   *  read straight after `draw` as `weaponAnchor` is: the bone's pose times its
   *  bind, placed by the body's model matrix, and `basis` that matrix's three
   *  axes at unit length, column-major -- the rotation `UpdateAttachingInstances`
   *  hands the effect with its position (`ActorInstanceAttach.cpp:553-563`). Null
   *  when that draw skinned no skeleton or the pack attaches nothing to that bone. */
  boneAnchor(key: string, bone: string): { xMm: number; yMm: number; zMm: number; basis: Float64Array } | null {
    if (this.skinnedBones === 0) return null;
    const part = `effect:${bone}`;
    const attach = (this.indexOf(key) ?? this.index)?.skins?.[key]?.attach?.[part];
    if (attach === undefined) return null;
    attachedModelMatrix(
      this.texels, attach.slot, this.bindWorldFor(key, part, attach),
      this.attachMatrix, this.attachScratch,
    );
    multiply(this.modelMatrix, this.attachMatrix, this.attachScratch);
    const m = this.attachScratch;
    const basis = new Float64Array(9);
    for (let column = 0; column < 3; column += 1) {
      const x = m[column * 4]!;
      const y = m[column * 4 + 1]!;
      const z = m[column * 4 + 2]!;
      const length = Math.hypot(x, y, z) || 1;
      basis[column * 3] = x / length;
      basis[column * 3 + 1] = y / length;
      basis[column * 3 + 2] = z / length;
    }
    return { xMm: m[12]!, yMm: m[13]!, zMm: m[14]!, basis };
  }

  hitWindowMs(entityId: number, key: string): readonly [number, number] | null {
    const skin = this.indexOf(key)?.skins?.[key];
    const instance = this.instances.peek(entityId);
    if (skin === undefined || instance === undefined || instance.once === null) return null;
    const playing = clipForSkin(skin, instance.once);
    const entry = playing === null ? undefined : skin.clips[playing];
    if (entry === undefined || entry.hitStartS === undefined || entry.hitEndS === undefined) {
      return null;
    }
    const ratio = instance.onceSpeedRatio > 0 ? instance.onceSpeedRatio : 1;
    return [
      instance.onceStartedMs + (entry.hitStartS * 1000) / ratio,
      instance.onceStartedMs + (entry.hitEndS * 1000) / ratio,
    ];
  }

  /** The clip entry for the one-shot this body is playing, or null. */
  playingClipEntry(entityId: number, key: string): ClipEntry | null {
    const skin = this.indexOf(key)?.skins?.[key];
    const instance = this.instances.peek(entityId);
    if (skin === undefined || instance === undefined || instance.once === null) return null;
    const playing = clipForSkin(skin, instance.once);
    return playing === null ? null : (skin.clips[playing] ?? null);
  }

  /** Clip metadata by skin key and clip name, for combo-window lookups that
   *  need the *previous* step's `inputLimitS` rather than the playing one. */
  clipEntry(key: string, clip: ClipName): ClipEntry | null {
    const skin = this.indexOf(key)?.skins?.[key];
    if (skin === undefined) return null;
    const playing = clipForSkin(skin, clip);
    return playing === null ? null : (skin.clips[playing] ?? null);
  }

  /** What race `race` is -- class, sex, and the bones it hangs weapons off --
   *  or null for a number that is not one of the original's eight. */
  race(race: number): RaceEntry | null {
    return this.index?.races?.[String(race)] ?? null;
  }

  baseBodyKey(race: number): string | null {
    return this.index === null ? null : baseBodyKeyIn(this.index, race);
  }

  /** What the main index says about the costume half, or null on a pack built
   *  without one. Read by `tools/e2e/costume.mjs`, which must be able to tell
   *  "this build ships no costume bodies" from "the fetch never happened". */
  costumePackRef(): CostumePackRef | null {
    return this.index?.costumePack ?? null;
  }

  /** The MAIN index's `raceBodies`, so `tools/e2e/costume.mjs` can prove that
   *  the key a costume resolved to came out of the second pack and out of
   *  nowhere else -- the main index is the one fetched whole at login, and a
   *  costume body having no row in it is the whole point of the split. */
  indexRaceBodies(): Readonly<Record<string, Readonly<Record<string, string>>>> | null {
    return this.index?.raceBodies ?? null;
  }

  indexRaceHair(): Readonly<Record<string, string>> | null {
    return this.index?.raceHair ?? null;
  }

  bodyModelKey(vnum: number, race: number = 0): string | null {
    if (this.index === null) return null;
    // The main pack first, then the costume pack -- and asking is what fetches
    // it. Null while it is in flight, which draws the bare body, exactly as a
    // shape the race's table has no row for does in the original
    // (`GameLib/ActorInstanceData.cpp:230-245`).
    return bodyModelKeyIn(this.index, vnum, race)
      ?? costumeBodyModelKeyIn(this.index, vnum, race);
  }

  defaultHairKey(race: number): string | null {
    return this.index === null ? null : defaultHairKeyIn(this.index, race);
  }

  /** The hair mesh a character with this `PART_HAIR` wears: the costume pack's
   *  row for the shape when it has one, else the race's own default hair.
   *  One lookup, so the local player and everybody else cannot disagree. */
  hairModelKey(race: number, hairPart = 0): string | null {
    if (this.index === null) return null;
    return costumeHairKeyIn(this.index, race, hairPart)
      ?? defaultHairKeyIn(this.index, race);
  }

  /** `hairShapeForVnumIn` off the loaded index -- the local player's worn
   *  `COSTUME_HAIR` vnum turned into the part the wire publishes for
   *  everybody else. */
  costumeHairShape(vnum: number): number {
    return this.index === null ? 0 : hairShapeForVnumIn(this.index, vnum);
  }

  status(key: string): MeshStatus {
    if (this.loadFailed !== null) {
      return { kind: "unavailable", reason: this.loadFailed };
    }
    if (this.gl === null) {
      return {
        kind: "unavailable",
        reason: this.sharedContext
          ? "shared terrain WebGL2 context unavailable for creature meshes"
          : "standalone WebGL2 context unavailable for creature meshes",
      };
    }
    if (this.index === null) return { kind: "loading" };
    // Either pack: a costume body is described by the costume index and its
    // bytes come out of the costume blob, and everything below is the same.
    const index = this.indexOf(key);
    if (index === null) {
      return { kind: "missing", reason: `${key} has no converted model` };
    }
    const skin = index.skins?.[key];
    if (skin !== undefined && this.animationFailed !== null) {
      // This model's vertices are at bind pose and the matrices that were to
      // stand it up never arrived. Drawing it anyway would put a T-posed
      // human in the middle of town; the silhouette is the honest answer.
      return { kind: "missing", reason: `${key} is skinned and ${this.animationFailed}` };
    }
    const shardFailure = skin === undefined ? undefined : this.animationFailures.get(skin.anim);
    if (shardFailure !== undefined) {
      return { kind: "missing", reason: `${key} is skinned and its clips never arrived: ${shardFailure}` };
    }
    const holder = this.packOf(key);
    const residency = holder?.require(key) ?? "resident";
    if (residency === "loading") return { kind: "loading" };
    if (residency === "failed") {
      // Not `unavailable`: the pack is fine and every other model in it still
      // draws. This is one model's window that will not come.
      const why = holder?.failureFor(key) ?? "the window never arrived";
      return { kind: "missing", reason: `${key}'s geometry could not be fetched: ${why}` };
    }
    if (skin !== undefined && skin.clips.idle === undefined) {
      return { kind: "missing", reason: `${key} is skinned but the pack carries no idle clip for it` };
    }
    // Both asked before either is answered: the skin textures and the clips
    // of a body are fetched together.
    const posed = skin === undefined || this.framesOf(skin) !== null;
    if (!this.skinsResident(key, index) || !posed) return { kind: "loading" };
    return { kind: "ready" };
  }

  private skinsResident(key: string, index: CreatureIndex): boolean {
    const model = index.models[key];
    if (model === undefined) return true;
    let resident = true;
    for (const mesh of model.meshes) {
      if (mesh.texture === null) continue;
      // Called for every mesh rather than stopping at the first miss, because
      // this is also the demand: a body with two skins must fetch both.
      if (this.textureFor(mesh.texture, this.index ?? index) !== null) continue;
      if (this.textures.get(mesh.texture) === "failed") continue;
      resident = false;
    }
    return resident;
  }

  beginFrame(pixelWidth: number, pixelHeight: number): void {
    const gl = this.gl;
    const canvas = this.canvas;
    if (gl === null || canvas === null) return;

    const now = performance.now();
    this.frameDeltaMs = this.lastFrameMs === 0 ? 16 : Math.min(500, now - this.lastFrameMs);
    this.lastFrameMs = now;
    this.instances.beginFrame();

    if (!this.sharedContext && canvas.parentNode === null && this.hostCanvas.parentNode !== null) {
      this.hostCanvas.parentNode.insertBefore(canvas, this.hostCanvas);
    }

    if (canvas.width !== pixelWidth || canvas.height !== pixelHeight) {
      canvas.width = pixelWidth;
      canvas.height = pixelHeight;
    }
    gl.viewport(0, 0, gl.drawingBufferWidth, gl.drawingBufferHeight);
    // Alpha zero: this canvas is a window onto the terrain canvas beneath it
    // (see the module docstring), and an opaque clear here would black out
    // every pixel no creature happens to cover.
    if (!this.sharedContext) {
      gl.clearColor(0, 0, 0, 0);
      gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);
    }
  }

  playOnce(
    entityId: number,
    key: string,
    clip: ClipName,
    nowMs: number,
    isMain = false,
    speedRatio = 1,
    then: ClipName | null = null,
  ): number {
    const skin = this.indexOf(key)?.skins?.[key];
    const playing: ClipName = skin === undefined ? clip : clipForSkin(skin, clip) ?? clip;
    if (skin === undefined || skin.clips[playing] === undefined) return 0;
    const variant = pickVariant(variantWeights(skin, playing), this.roll());
    const entry = this.entryOf(skin, playing, variant)!;
    // Clamped here rather than trusted, so a caller cannot stop or reverse a
    // motion by handing in a zero or a negative. `swingPlaybackRatio` is where
    // the number comes from and where the derivation is written down.
    const ratio = Math.min(
      MAX_ATTACK_SPEED_RATIO, Math.max(1, speedRatio > 0 ? speedRatio : 1));
    const runsForMs = (oneShotLengthS(entry) * 1000) / ratio;
    const instance = this.instances.get(entityId, 0, 0, key);
    if (instance.once === "dead") return runsForMs;
    // A body on the ground is not flinched or knocked down again
    // (`ActorInstanceBattle.cpp:782-783,825-826`, `ActorInstance.cpp:425-428`).
    if (instance.once !== null && isKnockDownClip(instance.once) && playing !== "dead") return runsForMs;
    const activeClip = instance.once ?? instance.clip;
    if (isDamageClip(playing) && !isMain && (activeClip === "walk" || activeClip === "run")) {
      return runsForMs;
    }
    instance.fadeFromVariant = instance.once !== null ? instance.onceVariant : instance.clipVariant;
    instance.fadeFrom = instance.once ?? instance.clip;
    instance.fadeFromPhase = this.phaseOf(instance, instance.fadeFrom, skin, nowMs);
    instance.fadeUntilMs = nowMs + CLIP_FADE_MS;
    instance.once = playing;
    instance.onceVariant = variant;
    const queued = then === null ? null : clipForSkin(skin, then);
    instance.onceThen = queued !== null && skin.clips[queued] !== undefined ? queued : null;
    instance.onceStartedMs = nowMs;
    instance.onceSpeedRatio = ratio;
    instance.onceEndsMs = nowMs + runsForMs;
    return runsForMs;
  }

  /** The shape an armour vnum dresses a body in, or null (`item.cpp:754-762`). */
  armourShape(vnum: number): number | null {
    return this.index?.armourShapes?.[String(vnum)] ?? null;
  }

  /** Draw this entity's loops in `mode` from its next pose on. A body not yet
   *  drawn has no instance to carry it, and gets it on its next frame. */
  setMotionMode(entityId: number, mode: MotionMode): void {
    const instance = this.instances.peek(entityId);
    if (instance !== undefined) instance.mode = mode;
  }

  revive(entityId: number): void {
    const instance = this.instances.peek(entityId);
    if (instance === undefined || instance.once !== "dead") return;
    instance.once = null;
    instance.onceThen = null;
    instance.onceEndsMs = 0;
    instance.fadeFrom = "dead";
    instance.fadeFromPhase = 1;
    instance.fadeUntilMs = performance.now() + CLIP_FADE_MS;
  }

  private poseFor(
    key: string,
    entityId: number,
    xMm: number,
    yMm: number,
    gait: Gait,
    skeletonKey: string = key,
    followEntityId: number | null = null,
  ): number {
    // `indexOf`, not `this.index`: a costume body's `skins` row is in the
    // second index, and its clips in the shard that row names (`framesOf`).
    const index = this.indexOf(skeletonKey) ?? this.index;
    if (index === undefined || index === null) return 0;

    const now = performance.now();
    // PART_HAIR follows the already-posed body on a derived entity id (see
    // `hairEntityId`). World position and heading come from the draw call.
    const leader = followEntityId !== null ? this.instances.peek(followEntityId) : undefined;
    if (leader !== undefined && followEntityId !== null) {
      this.instances.advance(entityId, xMm, yMm, now, this.frameDeltaMs);
      const hairSkin = index.skins?.[key];
      if (hairSkin === undefined) return 0;
      const hairFrames = this.framesOf(hairSkin);
      if (hairFrames === null) return 0;
      const bodySkin = index.skins?.[skeletonKey];
      const bodyFrames = bodySkin === undefined ? null : this.framesOf(bodySkin);
      const bones = hairSkin.boneCount;
      const width = valuesPerBone(hairSkin);
      const playing = clipForSkin(hairSkin, leader.once ?? leader.clip)
        ?? (hairSkin.clips.idle !== undefined ? "idle" : "walk");
      const clip = this.entryOf(hairSkin, playing, this.variantOf(leader, playing));
      if (clip === undefined) return 0;
      sampleClipInto(
        this.partPose, hairFrames, clip, bones,
        this.phaseOf(leader, playing, hairSkin, now), width,
      );
      let out = width;
      if (bodySkin !== undefined && bodyFrames !== null
          && this.writeSkinPose(skeletonKey, leader, bodySkin, now, bodyFrames) > 0) {
        const slots = this.partSlotsFor(skeletonKey, key, bodySkin, bodyFrames, hairSkin, hairFrames);
        const bodyWidth = valuesPerBone(bodySkin);
        out = Math.max(width, bodyWidth);
        if (out !== width) {
          // Widen in place, from the top down: bone `b` moves from `b*width`
          // to `b*out` with `out > width`, so descending never overwrites a
          // record that has not been read yet, and `copyPoseBone` fills the
          // new middle with an identity 3x3.
          for (let bone = bones - 1; bone >= 0; bone--) {
            copyPoseBone(this.partPose, bone, width, this.partPose, bone, out);
          }
        }
        for (let bone = 0; bone < bones; bone++) {
          const slot = slots[bone]!;
          if (slot >= 0) copyPoseBone(this.pose, slot, bodyWidth, this.partPose, bone, out);
        }
      }
      poseToBoneTexels(this.partPose, this.texels, bones, this.mirrorFlags(key, hairSkin, clip), out);
      return bones;
    }

    // `PART_HAIR` is not an independently animated character.  The original
    // creates it with PART_MAIN as its skeleton instance, so its vertices use
    // the body pose (and, in particular, the sword-combo pose) rather than the
    // one-bone general-motion stream stored alongside the hair model.
    const skin = index.skins?.[skeletonKey];
    if (skin === undefined) return 0;
    const animation = this.framesOf(skin);
    if (animation === null) return 0;

    const instance = this.instances.advance(entityId, xMm, yMm, now, this.frameDeltaMs, skeletonKey);
    const bones = skin.boneCount;

    // Retire a finished one-shot. `dead` is the exception and holds its last
    // frame: there is nothing to hand back to, and a corpse that stood up
    // after three seconds would be the most alarming bug in the game.
    let oneShot = instance.once === null ? null : clipForSkin(skin, instance.once);
    if (instance.once !== null && instance.once !== "dead" && oneShot !== null) {
      // The body started the one-shot and owns its duration. Hair can map a
      // one-hand sword attack to its general attack (`clipForSkin`), but its
      // local .msa duration is not authority to cut the body's swing short.
      const over = now >= instance.onceEndsMs;
      const next = over && instance.onceThen !== null ? skin.clips[instance.onceThen] : undefined;
      if (over && next !== undefined && instance.onceThen !== null) {
        // `ReservingMotionNode` starts the queued motion where the one ahead of
        // it ended (`ActorInstanceMotion.cpp:52-60,113-125`).
        instance.fadeFrom = instance.once;
        instance.fadeFromVariant = instance.onceVariant;
        instance.fadeFromPhase = 1;
        instance.fadeUntilMs = now + CLIP_FADE_MS;
        instance.once = instance.onceThen;
        instance.onceVariant = pickVariant(variantWeights(skin, instance.once), this.roll());
        instance.onceThen = null;
        instance.onceStartedMs = instance.onceEndsMs;
        instance.onceSpeedRatio = 1;
        instance.onceEndsMs = instance.onceStartedMs
          + oneShotLengthS(this.entryOf(skin, instance.once, instance.onceVariant) ?? next) * 1000;
        oneShot = clipForSkin(skin, instance.once);
      } else if (over) {
        instance.fadeFrom = instance.once;
        instance.fadeFromVariant = instance.onceVariant;
        instance.fadeFromPhase = 1;
        instance.fadeUntilMs = now + CLIP_FADE_MS;
        instance.once = null;
        instance.onceThen = null;
        instance.onceEndsMs = 0;
        oneShot = null;
      }
    }

    const moving = gait !== Gait.IDLE && now < instance.movingUntilMs;
    let loop: LoopName = "idle";
    if (moving) {
      const previous = instance.loop !== "idle" ? instance.loop : "walk";
      const chosen = selectLocomotion(instance.speedMmS, previous, skin.clips);
      loop = chosen === "walk" || chosen === "run" ? chosen : "idle";
    }
    instance.loop = loop;
    let wanted: ClipName = modeLoopClip(instance.mode, loop);
    if (skin.clips[wanted] === undefined) wanted = loop;
    if (skin.clips[wanted] === undefined) wanted = "idle";
    if (skin.clips[wanted] === undefined) return 0;

    if (wanted !== instance.clip) {
      // Only crossfade between loops here. A one-shot is playing over the
      // top; the loop underneath it may change during it (a creature hit
      // while running stops running) and that changeover is invisible until
      // the one-shot ends, which is exactly when the fade out of it starts.
      if (instance.once === null) {
        instance.fadeFromPhase = this.phaseOf(instance, instance.clip, skin, now);
        instance.fadeFrom = instance.clip;
        instance.fadeFromVariant = instance.clipVariant;
        instance.fadeUntilMs = now + CLIP_FADE_MS;
      }
      instance.clip = wanted;
      instance.clipVariant = pickVariant(variantWeights(skin, wanted), this.roll());
      instance.loopStartedMs = Number.NaN;
    }
    this.cycleLoopVariant(instance, skin, wanted, now);

    // A part which has no matching one-shot (for example a hair skin without
    // a damage motion) cannot decide that the body's shared one-shot ended.
    // It simply stays in its loop until the body retires that state.
    const playing = oneShot ?? wanted;
    const clip = this.entryOf(skin, playing, oneShot !== null ? instance.onceVariant : instance.clipVariant) as ClipEntry;
    const width = valuesPerBone(skin);
    sampleClipInto(this.pose, animation, clip, bones, this.phaseOf(instance, playing, skin, now), width);
    const fadeLeft = Math.min(CLIP_FADE_MS, instance.fadeUntilMs - now);
    const fadeFrom = clipForSkin(skin, instance.fadeFrom);
    if (fadeLeft > 0 && fadeFrom !== null && fadeFrom !== playing) {
      const from = this.entryOf(skin, fadeFrom, instance.fadeFromVariant)!;
      if (from !== undefined) {
        sampleClipInto(this.fadePose, animation, from, bones, instance.fadeFromPhase, width);
        blendPoseInto(this.pose, this.fadePose, fadeLeft / CLIP_FADE_MS, bones, width,
          this.mirrorFlags(skeletonKey, skin, clip), this.mirrorFlags(skeletonKey, skin, from));
      }
    }
    poseToBoneTexels(this.pose, this.texels, bones, this.mirrorFlags(skeletonKey, skin, clip), width);
    return bones;
  }

  /** Sample `instance`'s current clip (and fade) into `this.pose`. Does not
   *  advance or retire state -- the body draw already owns that. */
  private writeSkinPose(
    key: string, instance: Instance, skin: SkinEntry, now: number, animation: Int16Array,
  ): number {
    const oneShot = instance.once === null ? null : clipForSkin(skin, instance.once);
    let wanted: ClipName = instance.clip;
    if (skin.clips[wanted] === undefined) wanted = "idle";
    const playing = oneShot ?? wanted;
    const clip = this.entryOf(skin, playing, oneShot !== null ? instance.onceVariant : instance.clipVariant);
    if (clip === undefined) return 0;
    const bones = skin.boneCount;
    const width = valuesPerBone(skin);
    sampleClipInto(this.pose, animation, clip, bones, this.phaseOf(instance, playing, skin, now), width);
    // Bounded for `writeBodyPose`'s reason -- a lerp weight above one is an
    // extrapolation, not a crossfade.
    const fadeLeft = Math.min(CLIP_FADE_MS, instance.fadeUntilMs - now);
    const fadeFrom = clipForSkin(skin, instance.fadeFrom);
    if (fadeLeft > 0 && fadeFrom !== null && fadeFrom !== playing) {
      const from = this.entryOf(skin, fadeFrom, instance.fadeFromVariant);
      if (from !== undefined) {
        sampleClipInto(this.fadePose, animation, from, bones, instance.fadeFromPhase, width);
        blendPoseInto(this.pose, this.fadePose, fadeLeft / CLIP_FADE_MS, bones, width,
          this.mirrorFlags(key, skin, clip), this.mirrorFlags(key, skin, from));
      }
    }
    return bones;
  }

  /** `bodySlotsForHair` per body+part pair, worked out the first time that
   *  pair draws: it walks a whole idle clip against every bone in the body's
   *  palette, which is a few thousand comparisons -- once, not per frame. */
  private partSlotsFor(
    bodyKey: string, partKey: string,
    body: SkinEntry, bodyFrames: Int16Array, part: SkinEntry, partFrames: Int16Array,
  ): Int16Array {
    const slots = this.partSlots ?? (this.partSlots = new Map());
    const id = `${bodyKey}\0${partKey}`;
    const cached = slots.get(id);
    if (cached !== undefined) return cached;
    const found = bodySlotsForHair(body, bodyFrames, part, partFrames);
    slots.set(id, found);
    return found;
  }

  private mirrorFlags(key: string, skin: SkinEntry, clip: ClipEntry): Int8Array | null {
    const id = clip.mirroredBones === undefined ? key : `${key}\0${clip.offset}`;
    const cached = this.mirroredBones.get(id);
    if (cached !== undefined) return cached;
    const slots = clipMirroredBones(skin, clip);
    let flags: Int8Array | null = null;
    if (slots.length > 0) {
      flags = new Int8Array(skin.boneCount);
      for (const slot of slots) if (slot >= 0 && slot < skin.boneCount) flags[slot] = 1;
    }
    this.mirroredBones.set(id, flags);
    return flags;
  }

  /** `clip`'s row for file `variant`: `clip~variant`, or `clip`'s own where
   *  the skin has no such file. */
  private entryOf(skin: SkinEntry, clip: ClipName, variant: number): ClipEntry | undefined {
    const clips = skin.clips as Readonly<Record<string, ClipEntry | undefined>>;
    return (variant > 0 ? clips[`${clip}~${variant}`] : undefined) ?? clips[clip];
  }

  /** Which file of `clip` `instance` plays, as `pickVariant` last chose it. */
  private variantOf(instance: Instance, clip: ClipName): number {
    if (instance.once !== null && clip === instance.once) return instance.onceVariant;
    if (clip === instance.clip) return instance.clipVariant;
    return clip === instance.fadeFrom ? instance.fadeFromVariant : 0;
  }

  /** `random() % 100` (`ActorInstanceMotion.cpp:600`). */
  private roll(): number {
    return Math.floor((this.random ?? Math.random)() * 100); // ActorInstanceMotion.cpp:600
  }

  /** A standing loop with variants picks its file again each time a pass
   *  ends, the wait being pushed anew when one finishes
   *  (`ActorInstanceMotion.cpp:236-241`). */
  private cycleLoopVariant(instance: Instance, skin: SkinEntry, clip: ClipName, nowMs: number): void {
    if (!STANDING_LOOPS.has(clip)) return;
    const weights = variantWeights(skin, clip);
    if (weights.length <= 1) return;
    const passMs = (): number => (this.entryOf(skin, clip, instance.clipVariant)?.durationS ?? 0) * 1000;
    if (!Number.isFinite(instance.loopStartedMs) || nowMs - instance.loopStartedMs > 16 * passMs()) {
      instance.clipVariant = pickVariant(weights, this.roll());
      const first = passMs();
      instance.loopStartedMs = first > 0 ? nowMs - ((instance.idleOffsetS * 1000) % first) : nowMs;
      return;
    }
    while (passMs() > 0 && nowMs - instance.loopStartedMs >= passMs()) {
      instance.loopStartedMs += passMs();
      instance.clipVariant = pickVariant(weights, this.roll());
    }
  }

  /** Whether `entityId`'s playing one-shot has it hidden: past a hide event and
   *  not yet past a show (`hiddenAt`). A new motion shows it again
   *  (`ActorInstanceMotion.cpp:693-696`). */
  isHiding(entityId: number, key: string, nowMs: number): boolean {
    const instance = this.instances.peek(entityId);
    const skin = this.indexOf(key)?.skins?.[key];
    if (instance === undefined || instance.once === null || skin === undefined) return false;
    const entry = this.entryOf(skin, instance.once, instance.onceVariant);
    if (entry?.hideS === undefined) return false;
    const elapsedS = (Math.max(0, nowMs - instance.onceStartedMs) * instance.onceSpeedRatio) / 1000;
    return hiddenAt(entry, oneShotLocalS(entry, elapsedS));
  }

  private phaseOf(instance: Instance, clip: ClipName, skin: SkinEntry, nowMs: number): number {
    const entry = this.entryOf(skin, clip, this.variantOf(instance, clip));
    if (entry === undefined) return 0;
    if (entry.loop === false) {
      if (entry.durationS <= 0) return 1;
      const elapsed = Math.max(0, nowMs - instance.onceStartedMs) * instance.onceSpeedRatio;
      return Math.min(1, oneShotLocalS(entry, elapsed / 1000) / entry.durationS);
    }
    if (STANDING_LOOPS.has(clip)) {
      if (entry.durationS <= 0) return 0;
      if (variantWeights(skin, clip).length > 1 && Number.isFinite(instance.loopStartedMs)) {
        return (nowMs - instance.loopStartedMs) / 1000 / entry.durationS;
      }
      return (nowMs / 1000 + instance.idleOffsetS) / entry.durationS;
    }
    return instance.distanceMm / strideOf(entry);
  }

  /** Point every cast at `map`, or at nothing. */
  setCharacterShadow(map: CharacterShadowMap | null): void {
    this.characterShadow = map;
  }

  /** `CGraphicThingInstance::RenderToShadowMap` for the body `draw` drew last
   *  (`ModelInstanceRender.cpp:355-390`): its meshes and held parts, in the pose
   *  that draw left, flat grey from the character shadow light. A target on
   *  another context is not drawn into. */
  castShadow(key: string, held: Readonly<Record<string, string>> | null = null): void {
    const gl = this.gl;
    const program = this.program;
    const uniforms = this.uniforms;
    const map = this.characterShadow;
    if (gl === null || program === null || uniforms === null || map === null || map.gl !== gl) return;
    if (this.index === null) return;
    const index = this.indexOf(key) ?? this.index;
    const buffers = this.buffersFor(key);
    const model = index.models[key];
    if (model === undefined || buffers.vbo === null || buffers.ebo === null) return;

    map.bind();
    gl.useProgram(program);
    gl.bindVertexArray(this.vao);
    gl.enable(gl.DEPTH_TEST);
    gl.depthFunc(gl.LESS);
    gl.depthMask(true);
    gl.disable(gl.BLEND);
    gl.bindBuffer(gl.ARRAY_BUFFER, buffers.vbo);
    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, buffers.ebo);
    gl.uniform1f(uniforms.u_flat, 1);
    gl.uniform1i(uniforms.u_bones, 1);
    if (this.skinnedBones > 0 && this.boneTexture !== null) {
      gl.activeTexture(gl.TEXTURE1);
      gl.bindTexture(gl.TEXTURE_2D, this.boneTexture);
    }
    gl.uniform1f(uniforms.u_skinned, this.skinnedBones > 0 ? 1 : 0);
    multiply(map.capturing, this.modelMatrix, this.shadowMvp);
    gl.uniformMatrix4fv(uniforms.u_modelViewProj, false, this.shadowMvp);
    const attributes = index.vertexStride >= 40 ? VERTEX_SPEC : VERTEX_SPEC.slice(0, 3);
    this.drawFlat(model.meshes, index, attributes);

    const attachments = held === null || this.skinnedBones === 0 ? undefined : index.skins?.[key]?.attach;
    if (held !== null && attachments !== undefined) {
      const main = this.index;
      gl.bindBuffer(gl.ARRAY_BUFFER, this.vbo);
      gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, this.ebo);
      gl.uniform1f(uniforms.u_skinned, 0);
      for (const [part, itemKey] of Object.entries(held)) {
        const attach = attachSlot(attachments, part);
        const item = main.models[itemKey];
        if (attach === undefined || item === undefined || this.status(itemKey).kind !== "ready") continue;
        attachedModelMatrix(
          this.texels, attach.slot, this.bindWorldFor(key, part, attach),
          this.attachMatrix, this.attachScratch,
        );
        multiply(this.shadowMvp, this.attachMatrix, this.attachedMvp);
        gl.uniformMatrix4fv(uniforms.u_modelViewProj, false, this.attachedMvp);
        this.drawFlat(item.meshes, main, attributes);
      }
    }
    gl.uniform1f(uniforms.u_flat, 0);
    map.unbind();
  }

  private drawFlat(
    meshes: readonly MeshEntry[],
    index: CreatureIndex,
    attributes: readonly AttributeSpec[],
  ): void {
    const gl = this.gl;
    const program = this.program;
    if (gl === null || program === null) return;
    for (const mesh of meshes) {
      describeAttributes(gl, program, index.vertexStride, attributes.map((spec) => ({
        ...spec,
        offset: mesh.vertexOffset + spec.offset,
      })));
      const type = mesh.indexType === "uint16" ? gl.UNSIGNED_SHORT : gl.UNSIGNED_INT;
      gl.drawElements(gl.TRIANGLES, mesh.indexCount, type, mesh.indexOffset);
    }
  }

  /** `CActorInstance::SetMaterialColor` (`ActorInstanceRender.cpp:18-25`): the
   *  colour every part drawn for this entity id is lit by, until it is set again
   *  or forgotten. A part drawn on another body's skeleton takes that body's. */
  setShade(entityId: number, shade: readonly [number, number, number]): void {
    this.shades.set(entityId, shade);
  }

  forgetShade(entityId: number): void {
    this.shades.delete(entityId);
  }

  draw(
    key: string,
    entityId: number,
    xMm: number,
    yMm: number,
    zMm: number,
    headingSteps: number,
    gait: Gait,
    viewProj: Mat4,
    tint: CreaturePalette,
    held: Readonly<Record<string, string>> | null = null,
    skeletonKey: string = key,
    followEntityId: number | null = null,
    alpha = 1,
    modulate = 0,
  ): void {
    const gl = this.gl;
    const program = this.program;
    const uniforms = this.uniforms;
    if (gl === null || this.index === null || program === null || uniforms === null) return;
    if (this.vbo === null || this.ebo === null) return;
    // Windows that landed since the last draw, and the costume pack itself if
    // it has landed. Before the lookups below, so the first frame after the
    // costume index arrives can draw from it. Cheap when there is nothing new,
    // which is every frame after the first few; see `syncBuffers`.
    this.syncBuffers();
    // Whichever of the two packs holds this body: `index` describes its meshes,
    // bones and attachment slots, and the buffer pair is the one its bytes were
    // uploaded into. See `packOf`.
    const index = this.indexOf(key) ?? this.index;
    const buffers = this.buffersFor(key);
    if (buffers.vbo === null || buffers.ebo === null) return;
    const model = index.models[key];
    if (model === undefined) return;

    // A seated rider and his hair stand where the saddle is, not on the ground.
    const seat = this.seats.get(followEntityId ?? entityId);
    if (seat !== undefined) this.modelMatrix.set(seat);
    else creatureModelMatrix(xMm, yMm, zMm, headingSteps, this.modelMatrix);
    multiply(viewProj, this.modelMatrix, this.mvp);
    this.normalMatrix[0] = this.modelMatrix[0];
    this.normalMatrix[1] = this.modelMatrix[1];
    this.normalMatrix[2] = this.modelMatrix[2];
    this.normalMatrix[3] = this.modelMatrix[4];
    this.normalMatrix[4] = this.modelMatrix[5];
    this.normalMatrix[5] = this.modelMatrix[6];
    this.normalMatrix[6] = this.modelMatrix[8];
    this.normalMatrix[7] = this.modelMatrix[9];
    this.normalMatrix[8] = this.modelMatrix[10];

    gl.useProgram(program);
    gl.bindVertexArray(this.vao);
    gl.enable(gl.DEPTH_TEST);
    gl.depthFunc(gl.LESS);
    gl.bindBuffer(gl.ARRAY_BUFFER, buffers.vbo);
    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, buffers.ebo);
    gl.uniformMatrix4fv(uniforms.u_modelViewProj, false, this.mvp);
    gl.uniformMatrix3fv(uniforms.u_normalMatrix, false, this.normalMatrix);
    gl.uniform3f(uniforms.u_tint, ...hexToUnit(tint.body));
    const shade = this.shades.get(followEntityId ?? entityId) ?? FULL_SHADE;
    gl.uniform3f(uniforms.u_lightDiffuse, ...shadedDiffuse(characterLight.diffuse, shade));
    gl.uniform3f(uniforms.u_lightEmissive, ...characterLight.emissive);
    // A body part-way through its alpha ramp is the original's
    // RENDER_MODE_BLEND pass: blended against what is already there, and not
    // writing depth, so its own far side does not punch a hole in its near one.
    const blended = alpha < 1;
    gl.uniform1f(uniforms.u_alpha, alpha);
    gl.uniform3f(uniforms.u_modulate, ...modulateColour(modulate, alpha));
    if (blended) {
      gl.enable(gl.BLEND);
      gl.blendFuncSeparate(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA, gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
      gl.depthMask(false);
    }
    // Bound whether or not this model skins: an unset sampler reads as unit
    // 0, which is where the diffuse texture lives, and two samplers pointed
    // at one unit is the kind of thing a driver is entitled to complain
    // about even when the branch that would read it is never taken.
    gl.uniform1i(uniforms.u_bones, 1);

    const bones = this.boneTexture === null
      ? 0
      : this.poseFor(key, entityId, xMm, yMm, gait, skeletonKey, followEntityId);
    this.skinnedBones = bones;
    const saddle = bones > 0 ? index.skins?.[key]?.attach?.saddle : undefined;
    this.lastSaddle = saddle === undefined ? null : saddleModelMatrix(
      this.modelMatrix, this.texels, saddle.slot, this.bindWorldFor(key, "saddle", saddle),
      this.saddleWorld, this.saddleBone, this.saddleScratch,
    );
    if (bones > 0) {
      gl.activeTexture(gl.TEXTURE1);
      gl.bindTexture(gl.TEXTURE_2D, this.boneTexture);
      gl.texSubImage2D(gl.TEXTURE_2D, 0, 0, 0, 3, bones, gl.RGBA, gl.FLOAT, this.texels);
      gl.uniform1f(uniforms.u_skinned, 1);
    } else {
      // A model with no clips: its vertices already stand in frame 0 of its
      // own idle (or its bind pose, if it has not even that), baked in at
      // conversion time. Exactly what this renderer drew before it could
      // skin anything.
      gl.uniform1f(uniforms.u_skinned, 0);
    }

    // A pack whose vertices are narrower than 40 bytes carries no bone data,
    // and pointing the two bone attributes past the end of every vertex would
    // read whatever the next vertex's position happens to be as a bone index.
    // No such pack is written any more; the guard costs one comparison.
    const attributes = index.vertexStride >= 40 ? VERTEX_SPEC : VERTEX_SPEC.slice(0, 3);
    for (const mesh of model.meshes) {
      describeAttributes(gl, program, index.vertexStride, attributes.map((spec) => ({
        ...spec,
        offset: mesh.vertexOffset + spec.offset,
      })));

      const texture = mesh.texture !== null
        ? this.textureFor(mesh.texture, this.index)
        : null;
      if (texture !== null) {
        gl.activeTexture(gl.TEXTURE0);
        gl.bindTexture(gl.TEXTURE_2D, texture);
        gl.uniform1i(uniforms.u_texture, 0);
        gl.uniform1f(uniforms.u_hasTexture, 1);
      } else {
        gl.uniform1f(uniforms.u_hasTexture, 0);
      }

      const type = mesh.indexType === "uint16" ? gl.UNSIGNED_SHORT : gl.UNSIGNED_INT;
      gl.drawElements(gl.TRIANGLES, mesh.indexCount, type, mesh.indexOffset);
    }

    if (held !== null && bones > 0) {
      // Cleared here rather than at the top of `draw`, because a character is
      // several draws -- body, then `PART_HAIR` -- and the hair pass holds
      // nothing. Clearing unconditionally would wipe the body's answer before
      // `entities.ts` records it.
      this.heldOffsetMm = null;
      this.lastWeaponAnchor = null;
      this.lastWeaponAnchorLeft = null;
      this.lastWeaponTip = null;
      this.lastWeaponTipLeft = null;
      const attachments = index.skins?.[key]?.attach;
      if (attachments !== undefined) {
        // A weapon is never a costume body, so every held mesh comes out of the
        // MAIN pack -- and if this body came out of the costume pack, its
        // buffers are the ones currently bound. Rebind before drawing from them.
        const main = this.index;
        gl.bindBuffer(gl.ARRAY_BUFFER, this.vbo);
        gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, this.ebo);
        for (const [part, itemKey] of Object.entries(held)) {
          const attach = attachSlot(attachments, part);
          const item = main.models[itemKey];
          if (attach === undefined || item === undefined) continue;
          if (this.status(itemKey).kind !== "ready") continue;
          this.drawHeld(item, attach, key, part, main, attributes);
        }
      }
    }

    if (blended) {
      gl.disable(gl.BLEND);
      gl.depthMask(true);
      gl.uniform1f(uniforms.u_alpha, 1);
    }
  }

  private drawHeld(
    item: { readonly meshes: readonly MeshEntry[] },
    attach: AttachEntry,
    key: string,
    part: string,
    index: CreatureIndex,
    attributes: readonly AttributeSpec[],
  ): void {
    const gl = this.gl;
    const program = this.program;
    const uniforms = this.uniforms;
    if (gl === null || program === null || uniforms === null) return;

    attachedModelMatrix(
      this.texels, attach.slot, this.bindWorldFor(key, part, attach),
      this.attachMatrix, this.attachScratch,
    );
    // Where that hand ended up relative to the body's own feet, before the
    // world matrix puts the body anywhere. See `heldOffset` -- this is the
    // one number that tells a live browser whether a swing is being drawn on
    // the character or a chain-length in front of it.
    this.heldOffsetMm = Math.max(
      this.heldOffsetMm ?? 0,
      Math.hypot(this.attachMatrix[12]!, this.attachMatrix[13]!),
    );
    // `this.mvp` is already viewProj * model for the body; the item's own
    // transform composes on its right, which is what puts it in the hand
    // before the character is put in the world.
    multiply(this.mvp, this.attachMatrix, this.attachedMvp);
    multiply(this.modelMatrix, this.attachMatrix, this.attachScratch);
    // The attach bone's world position -- the product's translation -- kept
    // the way `heldOffsetMm` is: scratch for the body just drawn, read by
    // `entities.ts` straight after `draw`. `__AttachEffect` hangs the refine
    // glow and the buff loops on this bone (`playersettingmodule.py:357,391`).
    if (part === "weapon" || this.lastWeaponAnchor === null) {
      this.lastWeaponAnchor = {
        xMm: this.attachScratch[12]!,
        yMm: this.attachScratch[13]!,
        zMm: this.attachScratch[14]!,
      };
    }
    // The dagger's second copy: `weapon` and `weaponLeft` are drawn as two
    // separate calls to this method for the one body, so the off-hand bone
    // is simply whichever call this one is -- no fallback needed, unlike
    // `lastWeaponAnchor` above, because nothing else ever writes this field.
    if (part === "weaponLeft") {
      this.lastWeaponAnchorLeft = {
        xMm: this.attachScratch[12]!,
        yMm: this.attachScratch[13]!,
        zMm: this.attachScratch[14]!,
      };
    }
    // `CWeaponTrace::Update`'s tip: `m_fLength` out along the weapon's own z
    // from its root (`WeaponTrace.cpp:83-110`).
    const lengthMm = weaponTraceLengthMm(item.meshes);
    const tip = {
      xMm: this.attachScratch[8]! * lengthMm + this.attachScratch[12]!,
      yMm: this.attachScratch[9]! * lengthMm + this.attachScratch[13]!,
      zMm: this.attachScratch[10]! * lengthMm + this.attachScratch[14]!,
    };
    if (part === "weaponLeft") this.lastWeaponTipLeft = tip;
    else if (part === "weapon" || this.lastWeaponTip === null) this.lastWeaponTip = tip;
    for (let column = 0; column < 3; column++) {
      for (let row = 0; row < 3; row++) {
        this.attachedNormal[column * 3 + row] = this.attachScratch[column * 4 + row];
      }
    }
    gl.uniformMatrix4fv(uniforms.u_modelViewProj, false, this.attachedMvp);
    gl.uniformMatrix3fv(uniforms.u_normalMatrix, false, this.attachedNormal);
    gl.uniform1f(uniforms.u_skinned, 0);

    for (const mesh of item.meshes) {
      describeAttributes(gl, program, index.vertexStride, attributes.map((spec) => ({
        ...spec,
        offset: mesh.vertexOffset + spec.offset,
      })));
      const texture = mesh.texture !== null ? this.textureFor(mesh.texture, index) : null;
      if (texture !== null) {
        gl.activeTexture(gl.TEXTURE0);
        gl.bindTexture(gl.TEXTURE_2D, texture);
        gl.uniform1i(uniforms.u_texture, 0);
        gl.uniform1f(uniforms.u_hasTexture, 1);
      } else {
        gl.uniform1f(uniforms.u_hasTexture, 0);
      }
      const type = mesh.indexType === "uint16" ? gl.UNSIGNED_SHORT : gl.UNSIGNED_INT;
      gl.drawElements(gl.TRIANGLES, mesh.indexCount, type, mesh.indexOffset);
    }
  }

  private bindWorldFor(key: string, part: string, attach: AttachEntry): Mat4 {
    const cacheKey = `${key}/${part}`;
    let matrix = this.bindWorlds.get(cacheKey);
    if (matrix === undefined) {
      matrix = new Float32Array(attach.bindWorldMm);
      this.bindWorlds.set(cacheKey, matrix);
    }
    return matrix;
  }

  /** The GL texture for a mesh's `d:/ymir work/...` texture string, kicking
   *  off a fetch the first time it is asked for. Returns null while it is
   *  still loading or if it never resolves -- both draw with `tint` instead
   *  of stalling the whole frame on one image decode. */
  private textureFor(texturePath: string, index: CreatureIndex): WebGLTexture | null {
    const cached = this.textures.get(texturePath);
    if (cached === "loading" || cached === "failed" || cached === undefined) {
      if (cached === undefined) {
        const relative = index.textures[texturePath];
        if (relative === undefined) {
          this.textures.set(texturePath, "failed");
          return null;
        }
        const url = BASE_URL + relative;
        this.textures.set(texturePath, "loading");
        loadCreatureTextureWithRetry(() => loadImage(url))
          .then((image) => {
            const gl = this.gl;
            if (gl === null) return;
            const texture = gl.createTexture();
            if (texture === null) throw new GlError(`${url}: could not create texture object`);
            gl.activeTexture(gl.TEXTURE0);
            gl.bindTexture(gl.TEXTURE_2D, texture);
            gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, image);
            gl.generateMipmap(gl.TEXTURE_2D);
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR_MIPMAP_LINEAR);
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
            this.textures.set(texturePath, texture);
          })
          .catch((error) => {
            console.warn(`creature texture unavailable (${String(error)}); using a flat tint`);
            this.textures.set(texturePath, "failed");
          });
      }
      return null;
    }
    return cached;
  }
}

/** Prefer the terrain context; request a standalone one only for direct callers. */
export function resolveCreatureContext(
  sharedGl: WebGL2RenderingContext | null,
  canvas: HTMLCanvasElement,
): WebGL2RenderingContext | null {
  return sharedGl ?? canvas.getContext("webgl2", {
    alpha: true, antialias: true, depth: true, preserveDrawingBuffer: true,
  });
}

/** `#rrggbb` -> [r, g, b] in 0..1, for a uniform. Falls back to a neutral
 *  grey on anything else so a malformed palette entry tints rather than
 *  crashes -- the same discipline `entities.ts`'s own `shade()` applies. */
function hexToUnit(colour: string): readonly [number, number, number] {
  const match = /^#([0-9a-f]{6})$/i.exec(colour);
  if (match === null) return [0.5, 0.5, 0.5];
  const value = Number.parseInt(match[1], 16);
  return [((value >> 16) & 0xff) / 255, ((value >> 8) & 0xff) / 255, (value & 0xff) / 255];
}
